import {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useQueries, useQuery, useQueryClient } from "react-query";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { featureFlags } from "../../config/featureFlags";
import { BudgetService } from "../../services/budget.service";
import { ClosingControlService } from "../../services/closing-control.service";
import SupplierService from "../../services/supplier.service";
import { AuthContext } from "../auth";
import {
  ClosingControlState,
  ClosingStatus,
} from "../../types/closing-control.type";
import {
  isProjectAdmin,
  normalizeClosingControlState,
  resolveClosingControlState,
} from "../../utils/resolveClosingControl";
import {
  pickProjectClosingStatus,
  selectClosingBudgetIds,
} from "../../utils/projectClosingStatus";
import {
  closingApprovalTransitionRef,
  INITIAL_CLOSING_CONTROL_STATE,
} from "../../utils/closingApprovalTransition";
import {
  fetchBudgetDetail,
  fetchBudgetList,
  fetchBudgetSummaryList,
  fetchBudgetTotals,
  patchBudgetInListCache,
  setBudgetDetailCache,
} from "../../queries/budget";
import { queryKeys } from "../../queries/keys";
import {
  AllTotals,
  Budget,
  BudgetItemTypeEnum,
  BudgetType,
  ByItemType,
  HeaderOptions,
  ItemTypeListPerCalcOption,
  versionInfo,
  VersionTotals,
} from "../../types/budget/budget";
import { BudgetSummary } from "../../types/budget/budget-summary";
import type { ServiceWriteTotalsAttachment } from "../../types/budget/budget-totals";
import { ServiceData, ServiceValues } from "../../types/budget/service-item";
import {
  BudgetListItem,
  budgetToSummary,
  isFullBudget,
  mergeSummaryWithDetail,
  toBudgetListItem,
} from "../../utils/budget-adapters";
import {
  captureAddServiceFlushEpoch,
  holdAddServiceUntilFlush,
  notifyServiceAdded,
  releaseAddServiceFlushHolds,
  shouldAcceptAddService,
} from "../../utils/add-service-gesture";
import {
  buildBatchOperations,
  chunkBatchOperations,
  classifyServiceDeleteFailure,
  diffServiceStructuralChanges,
  getPhaseValuesFromService,
  isDeletableServiceId,
  refreshDirtyPhaseValuesFromBudget,
  shouldFlushAutosave,
  stripBudgetServicesForSlimPut,
  toCreateBudgetServiceBody,
} from "../../utils/budget-service-writes";
import {
  applyServerTotalsSlices,
  isTotalsVersionStale,
  pickWriteTotalsAttachment,
  preserveLocalTotalsTrees,
  resolveTotalsPhaseFromVersionInfo,
  totalsResponseToAttachment,
} from "../../utils/budget-server-totals";
import {
  applyHydrateServerTotals,
  backfillThenRebuildHydratedBudget,
  hasHydrateRebuildDrift,
  resolveHydrateVersionInfo,
  versionInfoForTotalsRebuild,
} from "../../utils/budget-hydrate-totals";
import {
  buildServiceIfMatch,
  isServiceVersionConflictError,
  normalizeUpdatedAtIso,
} from "../../utils/service-etag";
import { resolveServiceCalcOption } from "../../utils/budget-phase-calc-option";
import { ActiveBudgetAction, activeBudgetReducer } from "./reducer-funcion";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useProject } from "../project.context";
import { TabActive } from "../../types/project.type";

export type { BudgetListItem };

/**
 * Metadados de auditoria de um save explícito. Não são persistidos no
 * orçamento: o backend os retira do payload do `PUT /budget/update` e usa
 * apenas ao gravar o registro de auditoria — `actionType` vira o verbo da ação
 * e `auditContext` um resumo legível. Necessário para ações cujo diff
 * automático não conta a história (mover/transferir categoria só reescreve
 * chaves).
 */
export type BudgetAuditInfo = {
  actionType: string;
  auditContext?: Record<string, string>;
};
export { isFullBudget } from "../../utils/budget-adapters";

/**
 * Bloco de valores que a aba aberta edita — enviado ao backend como
 * `auditStage` para que o diff de auditoria seja calculado no bloco certo.
 *
 * O status do orçamento não serve para isso: um orçamento OPEN continua
 * editável na aba de Fechamento, gravando em `closingValues`. Sem essa
 * informação o backend comparava o bloco errado e o campo editado (o "Valor
 * unitário", por exemplo) sumia do registro de auditoria.
 */
export function auditStageFromTab(
  tab: TabActive
): "budget" | "negotiation" | "closing" | undefined {
  if (tab === TabActive.NEGOTIATION) return "negotiation";
  if (tab === TabActive.APPROVED) return "closing";
  if (tab === TabActive.BUDGET) return "budget";
  return undefined; // outras abas: o backend decide pelo status, como antes
}

/** Intervalo fixo entre tentativas de autosave enquanto houver alterações pendentes. */
const BUDGET_AUTOSAVE_INTERVAL_MS = 60000;

/** Debounce for coalescing per-service dirty patches before batch PATCH (FE-P3-03 / D-02). */
const BUDGET_SERVICE_WRITES_DEBOUNCE_MS = 750;

// Actions that come from the API / load path (not from a user edit). These
// must NOT bump the local-change revision counter, otherwise we'd schedule an
// autosave for a state that already matches the server.
const API_DRIVEN_ACTION_TYPES = new Set([
  "NEW_ACTIVE_BUDGET",
  "NO_ACTIVE_BUDGET",
  "UPDATE_FROM_API",
  // FE-P3-04: id remap / delete rollback must not mark the budget dirty again.
  "REPLACE_SERVICE_ID",
  "RESTORE_SERVICE",
  // Reparo de agregado legado (MMV-2511): recalcula em memória ao abrir a
  // versão. Não é edição do usuário — não pode marcar o orçamento como
  // alterado, senão dispara autosave e o aviso de "salve antes de trocar de aba".
  "RECALCULATE_TOTALS",
]);

/** Service field edits → v2 batch when `useV2ServiceWrites` (FE-P3-03). */
export const V2_BATCH_SERVICE_EDIT_TYPES = new Set([
  "UPDATE_SERVICE",
  "UPDATE_CALC_VALUES",
]);

/**
 * Bulk group-identity rewrites: persisted with a full-document PUT instead of
 * per-line v2 ops (see the classifier in `activeBudgetDispatch`).
 */
export const V2_BULK_GROUP_REWRITE_ACTION_TYPES = new Set([
  "MOVE_CATEGORY",
  "TRANSFER_CATEGORY_SUPPLIER",
  // Transferir um SERVIÇO reescreve a identidade de grupo de uma linha só, mas
  // vale o mesmo argumento de atomicidade: um delete+create que falhe no DELETE
  // deixaria o serviço duplicado nos dois fornecedores. Além disso a ação pode
  // criar categoria/fornecedor no destino e clonar `baseValues` —
  // o PUT do documento inteiro grava tudo de uma vez.
  "TRANSFER_SERVICE_SUPPLIER",
  // Agrupamento: as duas actions abaixo reescrevem `groupId` em VÁRIAS linhas de
  // `budgetServices` de uma vez. O PUT enxuto omite esse array de propósito
  // (as linhas são gravadas uma a uma pelos endpoints v2), então elas não podem
  // cair nele — o vínculo com o agrupamento se perderia em silêncio, e o bug
  // apareceria como "os serviços voltaram para o dia errado depois do reload".
  //
  // Não é delete+create: o conteúdo do serviço não muda, só o campo que diz a
  // que agrupamento ele pertence. Por isso vão pelo PUT do documento inteiro,
  // junto com as demais reescritas de identidade de grupo.
  "DELETE_GROUP",
  "MOVE_ROW_TO_GROUP",
  // Duplicar leva junto uma CÓPIA de cada serviço do agrupamento. São vários
  // inserts de uma vez; o PUT do documento inteiro grava todos numa transação
  // só, em vez de N POSTs que podem falhar no meio e deixar a cópia pela
  // metade.
  "DUPLICATE_GROUP",
  // Remover a linha apaga TODOS os serviços daquele agrupamento de uma vez, e
  // pode remover a categoria junto — mesmo argumento de atomicidade.
  "REMOVE_ROW_FROM_GROUP",
]);

/**
 * Actions that may add/remove/move lines in `budgetServices`.
 * When `useV2ServiceWrites`, these are flushed via POST/DELETE (+ delete+create
 * for group moves) instead of rewriting the array through a full v1 PUT.
 */
export const V2_STRUCTURAL_SERVICE_ACTION_TYPES = new Set([
  "ADD_SERVICE",
  "DELETE_SERVICE",
  // Só acrescenta um slot em `supplierList`. Fora daqui caía no PUT do
  // documento inteiro — mais pesado e mais sujeito a corrida com a seleção
  // do fornecedor (a linha preenchida era sobrescrita pela resposta vazia).
  "ADD_SUPPLIER",
  "UPDATE_SUPPLIER_LIST",
  "DELETE_BUDGET_ITEM",
  "DELETE_SUPPLIER",
  // Reordenações por drag and drop tocam só as listas de configuração
  // (`supplierList` / `addedItemTypeList`), nunca `budgetServices` — logo o PUT
  // enxuto basta. Fora deste Set elas ainda salvariam, mas via PUT do documento
  // completo.
  "REORDER_SUPPLIERS",
  "REORDER_CATEGORIES",
  // Agrupamento: mesma justificativa — estas tocam só `budgetGroups`, todo
  // dentro do PUT enxuto.
  //
  // `DELETE_GROUP`, `MOVE_ROW_TO_GROUP` e `DUPLICATE_GROUP` NÃO estão aqui de
  // propósito: elas reescrevem `budgetServices` em massa, que o PUT enxuto não
  // envia (ver V2_BULK_GROUP_REWRITE_ACTION_TYPES).
  "CREATE_GROUP",
  "RENAME_GROUP",
  "REORDER_GROUPS",
  // Criar a linha cria UM serviço, exatamente como `ADD_SERVICE` — o mesmo
  // caminho estrutural (POST do serviço + PUT enxuto das listas) dá conta.
  "ADD_ROW_TO_GROUP",
]);

// Valida se o orçamento está completamente carregado com todos os cálculos
function isBudgetFullyLoaded(budget: Budget | null | undefined): boolean {
  if (!budget || !budget._id) return false;

  // Verifica se totais principais foram calculados
  if (!budget.budgetValuesTotal?.byCalculationType) return false;
  if (!budget.budgetValuesTotal?.byItemType) return false;

  // Se não tem serviços, considera completo (orçamento vazio)
  if (!budget.budgetServices || budget.budgetServices.length === 0) {
    return true;
  }

  // Verifica se todos os serviços têm valores calculados
  const hasIncompleteServices = budget.budgetServices.some((service) => {
    // Serviço sem totais calculados
    if (!service.budgetValues?.totals) return true;
    // Serviço sem dados de impostos
    if (!service.budgetValues?.taxData) return true;
    return false;
  });
  return !hasIncompleteServices;
}

// Uma linha pode ser salva sem categoria, fornecedor ou forma de pagamento
// (Estudo de Verba). O que ainda não pode seguir para o autosave é um serviço
// sem o objeto de valores — isso chega incompleto da criação antiga da linha.
function hasDraftService(budget: Budget | null | undefined): boolean {
  const services = (budget as { budgetServices?: unknown[] } | null | undefined)
    ?.budgetServices;
  if (!Array.isArray(services) || services.length === 0) return false;

  return services.some((svc) => {
    if (!svc || typeof svc !== "object") return true;
    return !(svc as { budgetValues?: unknown }).budgetValues;
  });
}

function mergeEditableServiceMetadataFromLocal(
  apiBudget: Budget,
  localBudget: Budget
): Budget {
  if (!apiBudget?.budgetServices || !localBudget?.budgetServices) {
    return apiBudget;
  }

  const localServicesById = new Map(
    localBudget.budgetServices.map((service: any) => [service._id, service])
  );

  return {
    ...apiBudget,
    budgetServices: apiBudget.budgetServices.map((apiService: any) => {
      const localService: any = localServicesById.get(apiService._id);
      if (!localService) return apiService;

      let nextService = apiService;

      if (apiService.budgetValues && localService.budgetValues) {
        nextService = {
          ...nextService,
          budgetValues: {
            ...apiService.budgetValues,
            observation:
              localService.budgetValues.observation ??
              apiService.budgetValues.observation,
            serviceType:
              localService.budgetValues.serviceType ??
              apiService.budgetValues.serviceType,
            descriptions:
              localService.budgetValues.descriptions ??
              apiService.budgetValues.descriptions,
          },
        };
      }

      if (apiService.negotiationValue && localService.negotiationValue) {
        nextService = {
          ...nextService,
          negotiationValue: apiService.negotiationValue.map(
            (apiNeg: any, idx: number) => {
              const localNeg = localService.negotiationValue[idx];
              if (!localNeg) return apiNeg;
              return {
                ...apiNeg,
                observation: localNeg.observation ?? apiNeg.observation,
                serviceType: localNeg.serviceType ?? apiNeg.serviceType,
                descriptions: localNeg.descriptions ?? apiNeg.descriptions,
              };
            }
          ),
        };
      }

      if (apiService.closingValues && localService.closingValues) {
        nextService = {
          ...nextService,
          closingValues: {
            ...apiService.closingValues,
            observation:
              localService.closingValues.observation ??
              apiService.closingValues.observation,
            serviceType:
              localService.closingValues.serviceType ??
              apiService.closingValues.serviceType,
            descriptions:
              localService.closingValues.descriptions ??
              apiService.closingValues.descriptions,
          },
        };
      }

      return nextService;
    }),
  };
}

/**
 * Rebuild phase totals from services + category tax quotas.
 * Server/list snapshots often omit that overlay (extras-only or pre-line
 * trees), which made T. Geral snap back after add/save until Valores opened.
 */
function rebuildTotalsWithTaxOverlay(
  budget: Budget,
  versionInfo: versionInfo
): Budget {
  if (
    !Array.isArray(budget.budgetServices) ||
    budget.budgetServices.length === 0
  ) {
    return budget;
  }
  return activeBudgetReducer(budget, {
    type: "RECALCULATE_TOTALS",
    payload: {},
    versionInfo,
  });
}

export type BudgetContextProps = {
  notification?: any;
  loadingBudget?: boolean;
  /** True while a summary-row detail fetch is in flight (FE-P1-03). */
  isLoadingBudgetDetail: boolean;
  isLoadingCalcs: boolean;
  projectId?: string;
  /**
   * List rows for the sidebar.
   * - Flag off: full `Budget[]`
   * - Flag on: `BudgetSummary[]` (no `budgetServices`)
   * Typed as the union {@link BudgetListItem} so both paths compile.
   */
  budgetList?: BudgetListItem[];
  /**
   * Full editor document only — never a `BudgetSummary`.
   * Empty `{}` sentinel when unset (same as pre-refactor); not a list-row union.
   */
  activeBudget: Budget;
  activeBudgetIndex: number;
  negotiationVersion: number;
  activeCalcOption: HeaderOptions;
  hasNegotiations: boolean;
  hasClosings: boolean;
  detailedBudget: string;
  versionInfo: versionInfo;
  temp: any;
  isBudgetDisabled: boolean;
  categoriesSnapshot: any[];
  budgetAutosaveStatus: "saved" | "unsaved" | "saving" | "error";
  hasPendingBudgetChanges: boolean;
  hasBudgetAutosaveFailure: boolean;
  budgetAutosaveSuccessCount: number;
  isBudgetDataModalOpen: boolean;
  setIsBudgetDataModalOpen: (open: boolean) => void;
  runBudgetAutosave: (
    source?: "debounce" | "interval" | "navigation" | "manual",
    audit?: BudgetAuditInfo
  ) => Promise<boolean>;
  setCategoriesSnapshot: (snapshot: any[]) => void;
  createBudget: (budgetData: Budget) => void;
  /** Resolve com o resultado da API, ou `null` quando a gravação falha. */
  updateBudget: (
    budgetId: string,
    budgetData: Budget,
    action: string,
    version: number
  ) => Promise<any>;
  loadBudgetList: (projectId: string) => void;
  /**
   * Hydrate `activeBudget` from a list row.
   * Full docs dispatch immediately; summary rows fetch detail, merge, then dispatch.
   * Never dispatches `NEW_ACTIVE_BUDGET` with a summary-only payload.
   */
  hydrateActiveBudget: (
    listItem: BudgetListItem,
    stageVersion?: versionInfo
  ) => Promise<void>;
  closeNotification: () => void;
  showNotification: (message: string, severity: string) => void;
  clearBudget: () => void;
  createEmptyBudget: (type: BudgetType) => void;
  setIsLoadingCalcs: (value: boolean) => void;
  setLoadingBudget: (value: boolean) => void;
  activeBudgetDispatch: Dispatch<ActiveBudgetAction>;
  setNegotiationVersion: (version: number) => void;
  setActiveCalcOption: (option: HeaderOptions) => void;
  setDetailedBudget: (id: string) => void;
  setVersionInfo: (versionInfo: versionInfo) => void;
  handleTempTaxData: (value: any, type: string) => void;
  cleanTempTaxData: () => void;
  closingControl?: ClosingControlState;
  isLoadingClosingControl: boolean;
  isFetchingClosingControl: boolean;
  isClosingControlError: boolean;
  refetchClosingControl: () => Promise<unknown>;
  applyClosingControlState: (state: ClosingControlState) => void;
  /**
   * Status de fechamento do projeto (varre os orçamentos em Fechamento).
   * Independe da guia ativa e do orçamento selecionado.
   */
  resolveProjectClosingStatus: () => Promise<ClosingStatus>;
};

const BudgetContext = createContext<BudgetContextProps>(
  {} as BudgetContextProps
);

// -------------------------------------------------------------------------
// Slice contexts (Stage 2 split).
//
// The original `BudgetContext` carried the entire budget state plus every
// callback in a single value object. Any change to `activeBudget` (one per
// keystroke in a service input) invalidated that value reference and forced
// every `useBudget()` consumer in the tree to re-render — even leaves that
// only needed `versionInfo` or a stable dispatch.
//
// We now expose narrowly-scoped slice contexts whose values only change when
// their own data changes. Hot-path components (BudgetServiceItem,
// BudgetSupplier, ...) subscribe to the smallest slice they need, while
// existing callers can keep using `useBudget()` unchanged.
// -------------------------------------------------------------------------

export type BudgetMetaSlice = {
  activeBudgetId: string | undefined;
  status: Budget["status"];
  type: Budget["type"];
  name: Budget["name"];
  generalInfo: Budget["generalInfo"];
  versionHistory: Budget["versionHistory"];
  budgetIndex: Budget["budgetIndex"];
  negotiationVersion: number;
  versionInfo: versionInfo;
  detailedBudget: string;
  activeCalcOption: HeaderOptions;
  activeBudgetIndex: number;
  isBudgetDisabled: boolean;
  loadingBudget: boolean;
  isLoadingBudgetDetail: boolean;
  isLoadingCalcs: boolean;
  hasNegotiations: boolean;
  hasClosings: boolean;
  projectId: string | undefined;
};

export type BudgetServicesSlice = {
  budgetServices: Budget["budgetServices"];
  servicesById: Record<string, ServiceData>;
  servicesByGroupKey: Record<string, ServiceData[]>;
};

export type BudgetSuppliersSlice = {
  supplierList: Budget["supplierList"];
  /** Agrupamentos de fornecedores; vive nesta fatia porque muda junto com `supplierList`. */
  budgetGroups: NonNullable<Budget["budgetGroups"]>;
  addedItemTypeList: Budget["addedItemTypeList"];
  baseValues: Budget["baseValues"];
  mainAppliedTaxes: Budget["mainAppliedTaxes"];
  paymentSolicitation: Budget["paymentSolicitation"];
  files: Budget["files"];
  budgetWideAppliedTaxes: Budget["budgetWideAppliedTaxes"];
  itemTypeList: Budget["itemTypeList"];
};

export type BudgetTotalsSlice = {
  budgetValuesTotal: Budget["budgetValuesTotal"];
  negociationValuesTotal: Budget["negociationValuesTotal"];
  closingValuesTotal: Budget["closingValuesTotal"];
};

export type BudgetActionsSlice = {
  activeBudgetDispatch: Dispatch<ActiveBudgetAction>;
  setNegotiationVersion: (version: number) => void;
  setActiveCalcOption: (option: HeaderOptions) => void;
  setDetailedBudget: (id: string) => void;
  setVersionInfo: (versionInfo: versionInfo) => void;
  setLoadingBudget: (value: boolean) => void;
  setIsLoadingCalcs: (value: boolean) => void;
  setIsBudgetDataModalOpen: (open: boolean) => void;
  setCategoriesSnapshot: (snapshot: any[]) => void;
  runBudgetAutosave: (
    source?: "debounce" | "interval" | "navigation" | "manual",
    audit?: BudgetAuditInfo
  ) => Promise<boolean>;
  createBudget: (budgetData: Budget) => void;
  updateBudget: (
    budgetId: string,
    budgetData: Budget,
    action: string,
    version: number
  ) => any;
  loadBudgetList: (projectId: string) => void;
  hydrateActiveBudget: (
    listItem: BudgetListItem,
    stageVersion?: versionInfo
  ) => Promise<void>;
  clearBudget: () => void;
  createEmptyBudget: (type: BudgetType) => void;
  closeNotification: () => void;
  showNotification: (message: string, severity?: string) => void;
  handleTempTaxData: (value: any, type: string) => void;
  cleanTempTaxData: () => void;
};

export type BudgetUISlice = {
  notification: any;
  budgetAutosaveStatus: "saved" | "unsaved" | "saving" | "error";
  hasPendingBudgetChanges: boolean;
  hasBudgetAutosaveFailure: boolean;
  budgetAutosaveSuccessCount: number;
  isBudgetDataModalOpen: boolean;
  categoriesSnapshot: any[];
  budgetList: BudgetListItem[];
  temp: { current: any };
};

export type BudgetProvidersSlice = {
  /** Cached supplier metadata keyed by parsed supplier id. */
  providersById: Record<string, any>;
  /** True while at least one supplier query is in flight. */
  isLoadingProviders: boolean;
};

const BudgetMetaContext = createContext<BudgetMetaSlice>({} as BudgetMetaSlice);
const BudgetServicesContext = createContext<BudgetServicesSlice>(
  {} as BudgetServicesSlice
);
const BudgetSuppliersContext = createContext<BudgetSuppliersSlice>(
  {} as BudgetSuppliersSlice
);
const BudgetTotalsContext = createContext<BudgetTotalsSlice>(
  {} as BudgetTotalsSlice
);
const BudgetActionsContext = createContext<BudgetActionsSlice>(
  {} as BudgetActionsSlice
);
const BudgetUIContext = createContext<BudgetUISlice>({} as BudgetUISlice);
const BudgetProvidersContext = createContext<BudgetProvidersSlice>({
  providersById: {},
  isLoadingProviders: false,
});

// Mirror of the parsing logic used by per-row `useQuery`s before this
// context was lifted. Kept identical so cached entries stay reachable
// regardless of which component reads them.
function parseSupplierIdForProviderLookup(rawId: string): string {
  if (!rawId) return "";
  if (rawId.includes("|")) return rawId.split("|")[0];
  const parts = rawId.split("-");
  return parts.length > 1 ? parts[0] : rawId;
}

function getBudgetServiceGroupKey(
  budgetId: string,
  supplierId: string,
  type: string,
  calcOption: string,
  version: number
): string {
  return `${budgetId}::${supplierId}::${type}::${calcOption}::${version}`;
}

function BudgetProvider({ children }: any) {
  const { setTabActive, tabActive } = useProject();
  const { user, userRoleName } = useContext(AuthContext);
  const location = useLocation();
  const queryClient = useQueryClient();
  const [detailedBudget, setDetailedBudget] = useState("");
  const [versionInfo, setVersionInfoState] = useState<versionInfo>({
    comparisonVersion: 0,
    version: 0,
    status: "OPEN",
  });
  const versionInfoRef = useRef(versionInfo);
  // O render confirma o estado. O setter adianta o ref: a troca de aba chama
  // setVersionInfo e o hydrate no mesmo turno, e o recálculo lê o ref antes
  // do React pintar a etapa nova. Sem isso o Fechamento reconstruía a árvore
  // do Orçamento (v0, que sempre existe) e a tela abria em R$ 0,00.
  const setVersionInfo = useCallback((next: versionInfo) => {
    versionInfoRef.current = next;
    setVersionInfoState(next);
  }, []);
  versionInfoRef.current = versionInfo;

  // Kept in sync inside the reducer so batch dirty snapshots can read
  // post-recalc `taxData` in the same tick as `UPDATE_CALC_VALUES`.
  const latestActiveBudgetRef = useRef<Budget>({} as Budget);
  const [activeBudget, rawActiveBudgetDispatch] = useReducer(
    (state: Budget, action: ActiveBudgetAction): Budget => {
      const next = activeBudgetReducer(state, action);
      latestActiveBudgetRef.current = next;
      return next;
    },
    {} as Budget
  );

  // Revision-based dirty tracking. Bumped for every user-initiated action and
  // pinned to the saved baseline after a successful save. This replaces the
  // previous per-render `JSON.parse(JSON.stringify(activeBudget))` snapshot +
  // recursive `deepEqual` check, which fired on every keystroke and was the
  // single biggest contributor to input lag in large budgets.
  const localChangeRevisionRef = useRef<number>(0);
  const lastSavedRevisionRef = useRef<number>(0);

  /**
   * FE-P3-03: when `useV2ServiceWrites`, service field edits accumulate here
   * (latest values win per serviceId) and flush via batch PATCH after debounce.
   * Header / tax edits set `needsFullDocumentPutRef` for v1 PUT.
   *
   * FE-P3-04: create / hard-delete / group-move are derived at flush by
   * diffing `lastSavedServicesRef` vs current `budgetServices` (moves =
   * delete+create — BE PATCH has no group fields). Config side effects use
   * slim v1 PUT via `needsConfigPutRef`.
   */
  const dirtyServicesRef = useRef<Map<string, Partial<ServiceValues>>>(
    new Map()
  );
  const needsFullDocumentPutRef = useRef(false);
  /**
   * Slim v1 PUT (no budgetServices) for config after v2 structural ops.
   * With `USE_SERVER_TOTALS` off, also carries client totals trees (FE-P3-03).
   * With flag on (FE-P4-02), totals come from write-response / GET /totals —
   * slim PUT runs only when this ref is set (supplier/category lists).
   */
  const needsConfigPutRef = useRef(false);
  /**
   * O array inteiro de serviços precisa ser reescrito por um PUT do documento
   * (mover/transferir categoria). Enquanto ligado, as operações v2 por linha
   * são puladas: o PUT já carrega o array completo, e emular a mudança de
   * grupo com delete+create abre janela para linha duplicada.
   */
  const needsServicesRewriteRef = useRef(false);
  /** Baseline service lines after last successful save / load (FE-P3-04 diff). */
  const lastSavedServicesRef = useRef<ServiceData[]>([]);
  /**
   * FE-P3-05: local `updatedAt` ISO per serviceId for If-Match.
   * Seeded from v2 service list cache on load; advanced after successful writes.
   * Kept out of `ServiceData` allowlist so v1 PUTs never send collection fields.
   */
  const serviceUpdatedAtByIdRef = useRef<Map<string, string>>(new Map());
  const serviceWritesDebounceRef = useRef<number | null>(null);
  /** Set on 412. Automatic flushes stay quiet until a manual or navigation save. */
  const serviceVersionConflictHoldRef = useRef(false);

  const runBudgetAutosaveRef = useRef<
    (
      source?: "debounce" | "interval" | "navigation" | "manual",
      audit?: BudgetAuditInfo
    ) => Promise<boolean>
  >(() => Promise.resolve(false));

  const clearServiceWritesDebounce = useCallback(() => {
    if (serviceWritesDebounceRef.current != null) {
      window.clearTimeout(serviceWritesDebounceRef.current);
      serviceWritesDebounceRef.current = null;
    }
  }, []);

  const scheduleDebouncedServiceAutosave = useCallback(() => {
    if (serviceVersionConflictHoldRef.current) {
      return;
    }
    clearServiceWritesDebounce();
    serviceWritesDebounceRef.current = window.setTimeout(() => {
      serviceWritesDebounceRef.current = null;
      void runBudgetAutosaveRef.current("debounce");
    }, BUDGET_SERVICE_WRITES_DEBOUNCE_MS);
  }, [clearServiceWritesDebounce]);

  const resetV2ServiceWriteState = useCallback(() => {
    dirtyServicesRef.current.clear();
    needsFullDocumentPutRef.current = false;
    needsConfigPutRef.current = false;
    needsServicesRewriteRef.current = false;
    clearServiceWritesDebounce();
  }, [clearServiceWritesDebounce]);

  const pinLastSavedServices = useCallback(
    (services: ServiceData[] | undefined) => {
      lastSavedServicesRef.current = Array.isArray(services)
        ? services.map((s) => ({ ...s }))
        : [];
    },
    []
  );

  const pinServiceUpdatedAts = useCallback(
    (budgetId: string | undefined) => {
      if (!budgetId) {
        serviceUpdatedAtByIdRef.current.clear();
        return;
      }
      const cached = queryClient.getQueryData<Record<string, string>>(
        queryKeys.budgetServiceUpdatedAts(budgetId)
      );
      serviceUpdatedAtByIdRef.current = cached
        ? new Map(Object.entries(cached))
        : new Map();
    },
    [queryClient]
  );

  const rememberServiceUpdatedAt = useCallback(
    (
      serviceId: string | undefined | null,
      updatedAt: Date | string | null | undefined
    ) => {
      if (!serviceId) return;
      const iso = normalizeUpdatedAtIso(updatedAt);
      if (!iso) return;
      serviceUpdatedAtByIdRef.current.set(serviceId, iso);
      const budgetId = latestActiveBudgetRef.current?._id;
      if (budgetId) {
        const next = Object.fromEntries(serviceUpdatedAtByIdRef.current);
        queryClient.setQueryData(
          queryKeys.budgetServiceUpdatedAts(budgetId),
          next
        );
      }
    },
    [queryClient]
  );

  const forgetServiceUpdatedAt = useCallback(
    (serviceId: string | undefined | null) => {
      if (!serviceId) return;
      serviceUpdatedAtByIdRef.current.delete(serviceId);
      const budgetId = latestActiveBudgetRef.current?._id;
      if (budgetId) {
        queryClient.setQueryData(
          queryKeys.budgetServiceUpdatedAts(budgetId),
          Object.fromEntries(serviceUpdatedAtByIdRef.current)
        );
      }
    },
    [queryClient]
  );

  // Wrap dispatch so user-driven actions bump the revision counter; API/load
  // actions are excluded so they don't make the budget look dirty.
  // When `useV2ServiceWrites`, also classify edits for batch vs structural/config.
  const activeBudgetDispatch = useCallback(
    (action: ActiveBudgetAction) => {
      if (API_DRIVEN_ACTION_TYPES.has(action.type)) {
        if (
          action.type === "NEW_ACTIVE_BUDGET" ||
          action.type === "NO_ACTIVE_BUDGET"
        ) {
          resetV2ServiceWriteState();
          if (action.type === "NEW_ACTIVE_BUDGET") {
            const budget = action.payload as Budget | undefined;
            pinLastSavedServices(budget?.budgetServices);
            pinServiceUpdatedAts(budget?._id);
          } else {
            pinLastSavedServices([]);
            pinServiceUpdatedAts(undefined);
          }
        }
        rawActiveBudgetDispatch(action);
        return;
      }

      // ADD_SERVICE is gated at the call site by `dispatchAddService` so this
      // wrapper does not check it again (that would record the accept and then
      // reject the same click). ADD_ROW_TO_GROUP still enters here directly.
      if (
        action.type === "ADD_ROW_TO_GROUP" &&
        !shouldAcceptAddService(action.payload)
      ) {
        return;
      }

      if (action.type === "ADD_ROW_TO_GROUP") {
        holdAddServiceUntilFlush(action.payload);
      }

      const serviceCountBefore =
        latestActiveBudgetRef.current?.budgetServices?.length ?? 0;

      localChangeRevisionRef.current += 1;

      // Reducer first: UPDATE_CALC_VALUES payloads carry new tax/exchange
      // flags but still-stale `taxData`. Export reads persisted services, so
      // the dirty snapshot must come from the post-recalc line.
      rawActiveBudgetDispatch(action);

      if (action.type === "ADD_ROW_TO_GROUP") {
        const serviceCountAfter =
          latestActiveBudgetRef.current?.budgetServices?.length ?? 0;
        if (serviceCountAfter > serviceCountBefore) notifyServiceAdded();
      }

      if (featureFlags.useV2ServiceWrites) {
        if (V2_BATCH_SERVICE_EDIT_TYPES.has(action.type)) {
          const service = action.payload as ServiceData | undefined;
          const serviceId = service?._id;
          if (serviceId) {
            const phaseInfo = action.versionInfo ?? versionInfoRef.current;
            const nextService =
              latestActiveBudgetRef.current.budgetServices?.find(
                (s) => s._id === serviceId
              );
            const phaseValues = getPhaseValuesFromService(
              nextService ?? service,
              phaseInfo
            );
            if (phaseValues) {
              dirtyServicesRef.current.set(serviceId, phaseValues);
              scheduleDebouncedServiceAutosave();
            }
          }
        } else if (
          V2_BULK_GROUP_REWRITE_ACTION_TYPES.has(action.type) ||
          // MMV-2663: trocar o fornecedor de card com categoria movida na etapa
          // transfere categorias (ver `usesStageScopedSupplierCardSwap`). O
          // delete+create do v2 deixou a linha antiga no documento ao lado da
          // nova; grava como transferir categoria.
          (action.type === "UPDATE_SUPPLIER_LIST" &&
            action.payload?.stageScopedCardSwap)
        ) {
          // Mover/transferir categoria reescreve a identidade de grupo
          // (calcOption|type|supplierId) de VÁRIAS linhas de uma vez. O v2 só
          // sabe emular isso com um delete+create por serviço, que não é
          // atômico: se o DELETE falhar depois do POST, a linha antiga fica no
          // documento e a categoria passa a aparecer nos DOIS fornecedores no
          // export. Um PUT do documento inteiro grava o array de uma vez, a
          // partir do estado local — que por construção tem uma linha só.
          needsFullDocumentPutRef.current = true;
          needsServicesRewriteRef.current = true;
          scheduleDebouncedServiceAutosave();
        } else if (V2_STRUCTURAL_SERVICE_ACTION_TYPES.has(action.type)) {
          // Soft version removals still need full PUT (detected at flush via diff).
          // Hard create/delete/move → v2; config lists → slim PUT.
          needsConfigPutRef.current = true;
          scheduleDebouncedServiceAutosave();
        } else {
          // Header / tax / exchange edits: full-document PUT so baseValues and
          // budgetWideAppliedTaxes persist, then GET /totals refreshes trees.
          needsFullDocumentPutRef.current = true;
          scheduleDebouncedServiceAutosave();
        }
      }
    },
    [
      pinLastSavedServices,
      pinServiceUpdatedAts,
      resetV2ServiceWriteState,
      scheduleDebouncedServiceAutosave,
    ]
  );

  // Ref para controlar se há mudanças pendentes
  const hasPendingChangesRef = useRef<boolean>(false);
  const isAutosaveRunningRef = useRef(false);
  const shouldRunQueuedSaveRef = useRef(false);
  // Mirrors `isBudgetDisabled` for autosave (defined later via useMemo).
  // Read-only budgets must never PUT — including navigation flush.
  const isBudgetDisabledRef = useRef(false);
  const lastStatusRef = useRef<"saved" | "unsaved" | "saving" | "error">(
    "saved"
  );

  // Refs para controlar carregamento inicial e prevenir autosave prematuro
  const lastLoadedBudgetIdRef = useRef<string | null>(null);
  /**
   * MMV-2626-3: id do orçamento cujo recálculo de abertura divergiu do que
   * está gravado. Consumido pelo efeito logo após o baseline de carga (que
   * chama `markBudgetSaved` e apagaria uma marcação feita antes dele).
   */
  const pendingHydrateRebuildSaveRef = useRef<string | null>(null);
  const initialLoadCompleteRef = useRef<boolean>(false);
  const loadStartTimeRef = useRef<number>(0);
  const loadCompleteTimeoutRef = useRef<number | null>(null);

  const [budgetAutosaveStatus, setBudgetAutosaveStatus] = useState<
    "saved" | "unsaved" | "saving" | "error"
  >("saved");
  const [hasPendingBudgetChanges, setHasPendingBudgetChanges] =
    useState<boolean>(false);
  const [hasBudgetAutosaveFailure, setHasBudgetAutosaveFailure] =
    useState<boolean>(false);
  const [budgetAutosaveSuccessCount, setBudgetAutosaveSuccessCount] =
    useState<number>(0);

  // Controla se o aviso de tipo do orçamento já foi exibido para o orçamento
  // ativo. Evita exibir o modal repetidamente a cada ciclo de autosave.
  const budgetTypeWarningShownRef = useRef<boolean>(false);

  // Controla se o modal "Dados do evento" está aberto. Enquanto estiver,
  // autosaves automáticos (debounce/interval) são suspensos para não
  // sobrescrever os campos que o usuário está digitando no modal.
  const [isBudgetDataModalOpen, setIsBudgetDataModalOpenState] =
    useState<boolean>(false);
  const isBudgetDataModalOpenRef = useRef<boolean>(false);
  const setIsBudgetDataModalOpen = useCallback((open: boolean) => {
    isBudgetDataModalOpenRef.current = open;
    setIsBudgetDataModalOpenState(open);
  }, []);

  type budgetListAction = {
    type: "UPDATE_LIST";
    payload: BudgetListItem[];
  };

  function budgetListReducer(
    state: BudgetListItem[],
    action: budgetListAction
  ) {
    return action.payload;
  }

  const [budgetList, budgetListRDispatch] = useReducer(
    budgetListReducer,
    [] as BudgetListItem[]
  );
  const [negotiationVersion, setNegotiationVersion] = useState(-1);
  const [hasNegotiations, setHasNegotiations] = useState(false);
  const [hasClosings, setHasClosings] = useState(false);
  const [isClosingApprovalInProgress, setIsClosingApprovalInProgress] =
    useState(false);
  const [activeCalcOption, setActiveCalcOption] = useState<HeaderOptions>("A");
  const [loadingBudget, setLoadingBudget] = useState(false);
  const [isLoadingBudgetDetail, setIsLoadingBudgetDetail] = useState(false);
  const [isLoadingCalcs, setIsLoadingCalcs] = useState(false);
  const detailFetchGenRef = useRef(0);

  const [activeBudgetIndex, setActiveBudgetIndex] = useState<number>(0);
  const [projectId, setProjectId] = useState<string>();
  const [notification, setNotification] = useState<any>({
    open: false,
    message: "",
    severity: "success",
  });
  const [categoriesSnapshot, setCategoriesSnapshot] = useState<any[]>([]);

  const closingControlProjectId =
    projectId ||
    new URLSearchParams(location.search).get("projectId") ||
    undefined;
  const isClosingTabActive = tabActive === TabActive.APPROVED;
  // O fechamento é por orçamento: a query é chaveada pelo orçamento ativo.
  const closingControlBudgetId = activeBudget?._id;
  const closingControlQueryKey = useMemo(
    () =>
      [
        "closing-control",
        closingControlProjectId,
        closingControlBudgetId,
      ] as const,
    [closingControlProjectId, closingControlBudgetId]
  );

  const {
    data: closingControl,
    isLoading: isLoadingClosingControl,
    isFetching: isFetchingClosingControl,
    isError: isClosingControlError,
    refetch: refetchClosingControl,
  } = useQuery({
    queryKey: closingControlQueryKey,
    queryFn: async () => {
      const state = await ClosingControlService.get(
        closingControlProjectId!,
        closingControlBudgetId!
      );
      return normalizeClosingControlState(state)!;
    },
    enabled:
      !!closingControlProjectId &&
      !!closingControlBudgetId &&
      isClosingTabActive &&
      !isClosingApprovalInProgress,
    staleTime: 30_000,
    cacheTime: 5 * 60_000,
    retry: 1,
  });

  const isAdmin = isProjectAdmin(userRoleName, user?.role_id);

  const resolvedClosingControl = useMemo(
    () => resolveClosingControlState(closingControl, isAdmin),
    [closingControl, isAdmin]
  );

  const beginClosingApprovalTransition = useCallback(() => {
    closingApprovalTransitionRef.current = true;
    setIsClosingApprovalInProgress(true);

    if (!closingControlProjectId) {
      return;
    }

    queryClient.setQueryData(
      closingControlQueryKey,
      INITIAL_CLOSING_CONTROL_STATE
    );
  }, [closingControlProjectId, closingControlQueryKey, queryClient]);

  const endClosingApprovalTransition = useCallback(() => {
    closingApprovalTransitionRef.current = false;
    setIsClosingApprovalInProgress(false);
  }, []);

  const applyClosingControlState = useCallback(
    (state: ClosingControlState) => {
      if (!closingControlProjectId) return;
      const normalized = normalizeClosingControlState(state);
      if (!normalized) return;
      queryClient.setQueryData(closingControlQueryKey, normalized);
    },
    [closingControlProjectId, closingControlQueryKey, queryClient]
  );

  /**
   * Status de fechamento do PROJETO, para validar a ida do projeto a
   * Pré-Fechamento.
   *
   * A query `closing-control` acima só roda na guia Fechamento e para o
   * orçamento ativo — o modal "Alterar Status" vive em qualquer guia (e o
   * projeto pode ter mais de um orçamento em Fechamento). Aqui varremos os
   * orçamentos que estão na etapa de Fechamento, reaproveitando o cache do
   * react-query quando já houver um estado carregado.
   */
  const resolveProjectClosingStatus =
    useCallback(async (): Promise<ClosingStatus> => {
      if (!closingControlProjectId) {
        return closingControl?.closingStatus ?? null;
      }

      const budgetIds = selectClosingBudgetIds(budgetList);
      if (budgetIds.length === 0) {
        return closingControl?.closingStatus ?? null;
      }

      try {
        const states = await Promise.all(
          budgetIds.map((budgetId) =>
            queryClient.fetchQuery(
              ["closing-control", closingControlProjectId, budgetId] as const,
              async () =>
                normalizeClosingControlState(
                  await ClosingControlService.get(
                    closingControlProjectId,
                    budgetId
                  )
                )!,
              { staleTime: 30_000 }
            )
          )
        );

        return pickProjectClosingStatus(states);
      } catch (error) {
        // Sem resposta da API, mantém o que já estava em cache (se houver).
        return closingControl?.closingStatus ?? null;
      }
    }, [budgetList, closingControl, closingControlProjectId, queryClient]);

  const isBudgetAutosaveTab = useMemo(
    () =>
      tabActive === TabActive.BUDGET ||
      tabActive === TabActive.NEGOTIATION ||
      tabActive === TabActive.APPROVED,
    [tabActive]
  );

  // Verifica se o budget atual tem mudanças em relação ao último salvo via
  // contador de revisão (sem deep clone, sem deepEqual).
  const hasRealChanges = useCallback(
    (budgetToCheck?: Budget): boolean => {
      const budget = budgetToCheck || activeBudget;
      if (!budget?._id) {
        return false;
      }
      return localChangeRevisionRef.current !== lastSavedRevisionRef.current;
    },
    [activeBudget]
  );

  // Marca o budget como "salvo" pinando o revision baseline. Aceita um valor
  // explícito para resolver corridas em que o usuário edita durante o save:
  // `runBudgetAutosave` captura `localChangeRevisionRef.current` antes da
  // chamada à API e passa esse valor após a resposta.
  const markBudgetSaved = useCallback(
    (savedRevision?: number) => {
      lastSavedRevisionRef.current =
        savedRevision !== undefined
          ? savedRevision
          : localChangeRevisionRef.current;

      const stillDirty =
        localChangeRevisionRef.current !== lastSavedRevisionRef.current;
      hasPendingChangesRef.current = stillDirty;
      setHasPendingBudgetChanges(stillDirty);

      if (stillDirty) {
        if (lastStatusRef.current !== "unsaved") {
          lastStatusRef.current = "unsaved";
          setBudgetAutosaveStatus("unsaved");
        }
        return;
      }

      pinLastSavedServices(latestActiveBudgetRef.current?.budgetServices);
      setHasBudgetAutosaveFailure(false);
      lastStatusRef.current = "saved";
      setBudgetAutosaveStatus("saved");
    },
    [pinLastSavedServices]
  );

  /**
   * FE-P4-01: merge server totals slices into activeBudget + list/detail caches
   * without a full-budget recompute. No-op when `useServerTotals` is off.
   */
  const applyServerTotalsAttachment = useCallback(
    (
      attachment: ServiceWriteTotalsAttachment | undefined,
      sentRevision: number
    ) => {
      if (!featureFlags.useServerTotals || !attachment) return;
      const current = latestActiveBudgetRef.current;
      if (!current?._id) return;

      const sliced = applyServerTotalsSlices(current, attachment);
      // Write-response / GET /totals trees can lag the new line or omit the
      // category tax overlay (T. Tarifas 1000, T. Geral 100). Rebuild from
      // local services before painting the editor and list.
      const next = rebuildTotalsWithTaxOverlay(sliced, versionInfoRef.current);
      latestActiveBudgetRef.current = next;

      budgetListRDispatch({
        type: "UPDATE_LIST",
        payload: budgetList.map((el) =>
          el._id === next._id
            ? toBudgetListItem(next, el, featureFlags.useV2BudgetList)
            : el
        ),
      });
      patchBudgetInListCache(queryClient, projectId, next._id!, next);
      setBudgetDetailCache(queryClient, next);

      if (
        latestActiveBudgetRef.current?._id === next._id &&
        localChangeRevisionRef.current === sentRevision
      ) {
        activeBudgetDispatch({
          type: "NEW_ACTIVE_BUDGET",
          payload: { ...next, isApiUpdate: true },
        });
      }
    },
    [activeBudgetDispatch, budgetList, projectId, queryClient]
  );

  /**
   * FE-P4-01: apply write-response totals when present; on missing attachment
   * or forced refresh (e.g. after slim/full PUT), refetch `GET /totals` for
   * the active phase. `totalsVersion` mismatch vs local also triggers refetch.
   */
  const syncServerTotalsAfterSave = useCallback(
    async (
      budgetId: string,
      writeResponse: unknown,
      sentRevision: number,
      options?: { forceRefetch?: boolean }
    ) => {
      if (!featureFlags.useServerTotals || !budgetId) return;

      const attachment = pickWriteTotalsAttachment(writeResponse);

      if (attachment && !options?.forceRefetch) {
        applyServerTotalsAttachment(attachment, sentRevision);
        return;
      }

      const phaseParams = resolveTotalsPhaseFromVersionInfo(
        versionInfoRef.current
      );
      try {
        const totalsResponse = await fetchBudgetTotals(
          queryClient,
          budgetId,
          phaseParams
        );
        const localVersion = latestActiveBudgetRef.current?.totalsVersion;
        const remoteVersion = totalsResponse.totalsVersion;
        const versionMismatch =
          localVersion != null &&
          remoteVersion != null &&
          Number(localVersion) !== Number(remoteVersion);

        if (
          options?.forceRefetch ||
          !attachment ||
          versionMismatch ||
          isTotalsVersionStale(localVersion, remoteVersion)
        ) {
          applyServerTotalsAttachment(
            totalsResponseToAttachment(totalsResponse),
            sentRevision
          );
        }
      } catch (error) {
        console.error("[Budget][ServerTotals] Failed to refetch totals", {
          budgetId,
          phaseParams,
          error,
        });
      }
    },
    [applyServerTotalsAttachment, queryClient]
  );

  const runBudgetAutosave = useCallback(
    async (
      source: "debounce" | "interval" | "navigation" | "manual" = "manual",
      audit?: BudgetAuditInfo
    ): Promise<boolean> => {
      if (source === "manual" || source === "navigation") {
        serviceVersionConflictHoldRef.current = false;
      }
      if (!shouldFlushAutosave(source, serviceVersionConflictHoldRef.current)) {
        return false;
      }

      // Só bloqueia autosaves automáticos durante carregamento inicial
      if (
        (source === "debounce" || source === "interval") &&
        !initialLoadCompleteRef.current
      ) {
        console.debug("[Autosave] Bloqueado: carregamento em andamento");
        return false;
      }

      const budgetToSave = latestActiveBudgetRef.current;

      if (!budgetToSave?._id) {
        return true;
      }

      // Orçamento/negociação/fechamento read-only: UI blocks edits, so never
      // persist (interval, debounce, navigation, or manual). Clear stale dirty
      // so step changes are not blocked by a false "unsaved" state.
      if (isBudgetDisabledRef.current) {
        resetV2ServiceWriteState();
        markBudgetSaved();
        return true;
      }

      if (
        (source === "debounce" || source === "interval") &&
        !budgetToSave.type
      ) {
        if (!budgetTypeWarningShownRef.current) {
          budgetTypeWarningShownRef.current = true;
          Swal.fire({
            title: "Tipo de orçamento não selecionado",
            text: "É necessário selecionar o tipo do orçamento",
            icon: "warning",
            confirmButtonColor: "#3085d6",
            confirmButtonText: "Ok",
          });
        }
        return false;
      }

      const useV2Writes = featureFlags.useV2ServiceWrites;
      const hasDirtyServices = dirtyServicesRef.current.size > 0;
      const needsFullPut = needsFullDocumentPutRef.current;
      const needsConfigPut = needsConfigPutRef.current;
      // Com reescrita completa pendente, as ops por linha são puladas: o PUT do
      // documento grava o array inteiro de uma vez.
      const rewritesAllServices = needsServicesRewriteRef.current;
      const structuralDiff =
        useV2Writes && !rewritesAllServices
          ? diffServiceStructuralChanges(
              lastSavedServicesRef.current,
              budgetToSave.budgetServices
            )
          : null;
      const hasStructuralOps = Boolean(
        structuralDiff &&
          (structuralDiff.creates.length > 0 ||
            structuralDiff.deletes.length > 0 ||
            structuralDiff.moves.length > 0 ||
            structuralDiff.needsFullDocumentPut)
      );

      // Debounce flushes service batch + structural create/delete/move +
      // header/tax full-document PUT (so tax quota edits persist promptly and
      // GET /totals can replace optimistic trees without snap-back to stale list).
      // (FE-P4-02: totals slim PUT is skipped when USE_SERVER_TOTALS — server
      // owns trees via includeTotals write responses.)
      if (source === "debounce" && useV2Writes) {
        if (
          !hasDirtyServices &&
          !needsConfigPut &&
          !hasStructuralOps &&
          !rewritesAllServices &&
          !needsFullPut
        ) {
          return true;
        }
      } else if (!hasRealChanges(budgetToSave)) {
        hasPendingChangesRef.current = false;
        setHasPendingBudgetChanges(false);
        if (!hasBudgetAutosaveFailure) {
          lastStatusRef.current = "saved";
          setBudgetAutosaveStatus("saved");
        }
        return true;
      }

      // Suspende autosaves automáticos (debounce/interval) enquanto o modal
      // "Dados do evento" estiver aberto, evitando que o retorno da API
      // sobrescreva os campos que o usuário ainda está digitando. Saves
      // explícitos (manual/navigation) continuam passando.
      if (
        (source === "debounce" || source === "interval") &&
        isBudgetDataModalOpenRef.current
      ) {
        hasPendingChangesRef.current = true;
        setHasPendingBudgetChanges(true);
        if (lastStatusRef.current !== "unsaved") {
          lastStatusRef.current = "unsaved";
          setBudgetAutosaveStatus("unsaved");
        }
        return false;
      }

      // Adia autosaves automáticos (debounce/interval) enquanto houver
      // serviço em rascunho incompleto. Mantém o estado como "unsaved" para
      // reagendar e deixa saves explícitos (manual/navigation) passarem.
      if (
        (source === "debounce" || source === "interval") &&
        hasDraftService(budgetToSave)
      ) {
        hasPendingChangesRef.current = true;
        setHasPendingBudgetChanges(true);
        if (lastStatusRef.current !== "unsaved") {
          lastStatusRef.current = "unsaved";
          setBudgetAutosaveStatus("unsaved");
        }
        return false;
      }

      if (isAutosaveRunningRef.current) {
        shouldRunQueuedSaveRef.current = true;
        return false;
      }

      isAutosaveRunningRef.current = true;
      setBudgetAutosaveStatus("saving");
      // Holds taken while this request is in flight outlive this `finally`.
      const addServiceFlushEpoch = captureAddServiceFlushEpoch();

      // Captura a revisão local NO MOMENTO do envio. Se o usuário editar entre
      // o request e a resposta, `localChangeRevisionRef.current` ja terá
      // avançado e a próxima checagem de dirty (`hasRealChanges`) corretamente
      // reportará alterações pendentes em vez de marcar tudo como salvo.
      const sentRevision = localChangeRevisionRef.current;

      let saveSucceeded = false;
      // `actionType`/`auditContext` não são persistidos no documento — o backend
      // os desestrutura do payload e usa só na auditoria: o primeiro vira o
      // rótulo da ação, o segundo entra como resumo legível
      // (Api/src/modules/budget/service/budget.service.ts). Sem eles, ações
      // semânticas como mover/transferir categoria eram gravadas como "Update"
      // e o diff automático não mostrava a troca de dono/bloco.
      const withAuditInfo = <T extends object>(payload: T): T =>
        audit ? ({ ...payload, ...audit } as T) : payload;
      try {
        if (
          useV2Writes &&
          (hasDirtyServices ||
            needsFullPut ||
            needsConfigPut ||
            hasStructuralOps ||
            rewritesAllServices)
        ) {
          // --- FE-P3-03/04: structural POST/DELETE + batch PATCH + slim/full PUT ---
          let didBatch = false;
          let didStructural = false;
          const budgetId = budgetToSave._id;
          const includeTotals = featureFlags.useServerTotals;
          let lastTotalsWriteResponse: unknown;

          const remapDirtyServiceId = (fromId: string, toId: string) => {
            if (fromId === toId || !dirtyServicesRef.current.has(fromId))
              return;
            const values = dirtyServicesRef.current.get(fromId)!;
            dirtyServicesRef.current.delete(fromId);
            dirtyServicesRef.current.set(toId, values);
          };

          const remapServiceUpdatedAt = (fromId: string, toId: string) => {
            if (fromId === toId) return;
            const iso = serviceUpdatedAtByIdRef.current.get(fromId);
            if (iso == null) return;
            serviceUpdatedAtByIdRef.current.delete(fromId);
            serviceUpdatedAtByIdRef.current.set(toId, iso);
          };

          const replaceLocalServiceId = (fromId: string, toId: string) => {
            if (!fromId || !toId || fromId === toId) return;
            activeBudgetDispatch({
              type: "REPLACE_SERVICE_ID",
              payload: { fromId, toId },
            });
            const current = latestActiveBudgetRef.current;
            if (current?.budgetServices) {
              latestActiveBudgetRef.current = {
                ...current,
                budgetServices: current.budgetServices.map((s) =>
                  s._id === fromId ? { ...s, _id: toId } : s
                ),
              };
            }
            remapDirtyServiceId(fromId, toId);
            remapServiceUpdatedAt(fromId, toId);
          };

          if (structuralDiff?.needsFullDocumentPut) {
            needsFullDocumentPutRef.current = true;
          }

          // FE-P3-04 creates: POST → insert server `_id` without refetch
          for (const createdLocal of structuralDiff?.creates ?? []) {
            const tempId = createdLocal._id;
            if (!tempId) continue;
            const latest =
              latestActiveBudgetRef.current.budgetServices?.find(
                (s) => s._id === tempId
              ) ?? createdLocal;
            const sortOrder =
              latestActiveBudgetRef.current.budgetServices?.findIndex(
                (s) => s._id === tempId
              ) ?? -1;
            const created = await BudgetService.createBudgetServiceV2(
              budgetId,
              toCreateBudgetServiceBody(
                latest,
                sortOrder >= 0 ? sortOrder : undefined
              ),
              { includeTotals }
            );
            if (includeTotals) {
              lastTotalsWriteResponse = created;
            }
            const newId = created._id;
            if (newId && newId !== tempId) {
              replaceLocalServiceId(tempId, newId);
            }
            rememberServiceUpdatedAt(newId || tempId, created.updatedAt);
            // Drop dirty for brand-new line — POST already sent current values.
            if (newId) dirtyServicesRef.current.delete(newId);
            // Advance baseline so a mid-flush retry does not POST duplicates.
            lastSavedServicesRef.current = [
              ...lastSavedServicesRef.current.filter((s) => s._id !== tempId),
              {
                ...(latestActiveBudgetRef.current.budgetServices?.find(
                  (s) => s._id === (newId || tempId)
                ) ?? { ...latest, _id: newId || tempId }),
              },
            ];
            didStructural = true;
          }

          // FE-P3-04 moves: delete+create (BE PATCH has no group-identity fields)
          for (const move of structuralDiff?.moves ?? []) {
            const oldId = move.before._id;
            if (!oldId) continue;
            const latest =
              latestActiveBudgetRef.current.budgetServices?.find(
                (s) => s._id === oldId
              ) ?? move.after;
            const sortOrder =
              latestActiveBudgetRef.current.budgetServices?.findIndex(
                (s) => s._id === oldId
              ) ?? -1;
            const created = await BudgetService.createBudgetServiceV2(
              budgetId,
              toCreateBudgetServiceBody(
                latest,
                sortOrder >= 0 ? sortOrder : undefined
              ),
              { includeTotals }
            );
            if (includeTotals) {
              lastTotalsWriteResponse = created;
            }
            if (isDeletableServiceId(oldId)) {
              try {
                const deleteTotals = await BudgetService.deleteBudgetServiceV2(
                  budgetId,
                  oldId,
                  {
                    ifMatch: buildServiceIfMatch(
                      serviceUpdatedAtByIdRef.current.get(oldId)
                    ),
                    includeTotals,
                  }
                );
                if (includeTotals && deleteTotals) {
                  lastTotalsWriteResponse = deleteTotals;
                }
                forgetServiceUpdatedAt(oldId);
              } catch (deleteError) {
                // 404: a linha antiga já saiu do servidor — o move chegou ao
                // estado desejado, então segue para a contabilidade comum
                // logo abaixo em vez de desfazer o POST.
                if (
                  classifyServiceDeleteFailure(deleteError) ===
                  "already-deleted"
                ) {
                  forgetServiceUpdatedAt(oldId);
                } else {
                  // Sem rollback, a linha antiga sobrevive ao lado da nova e a
                  // categoria passa a aparecer nos DOIS fornecedores no export —
                  // invisível na tela, porque a listagem segue `addedItemTypeList`.
                  // Melhor desfazer o POST e falhar o save do que corromper o
                  // documento em silêncio.
                  console.error(
                    "[Budget][Autosave] Move DELETE failed after POST; rolling back the created line",
                    { budgetId, oldId, newId: created._id, deleteError }
                  );
                  if (created._id) {
                    try {
                      await BudgetService.deleteBudgetServiceV2(
                        budgetId,
                        created._id
                      );
                    } catch (rollbackError) {
                      console.error(
                        "[Budget][Autosave] Move rollback failed; duplicate line may exist",
                        { budgetId, newId: created._id, rollbackError }
                      );
                    }
                  }
                  throw deleteError;
                }
              }
            }
            if (created._id) {
              replaceLocalServiceId(oldId, created._id);
              rememberServiceUpdatedAt(created._id, created.updatedAt);
              dirtyServicesRef.current.delete(created._id);
            }
            lastSavedServicesRef.current = [
              ...lastSavedServicesRef.current.filter((s) => s._id !== oldId),
              {
                ...(latestActiveBudgetRef.current.budgetServices?.find(
                  (s) => s._id === created._id
                ) ?? { ...latest, _id: created._id }),
              },
            ];
            didStructural = true;
          }

          // FE-P3-04 deletes: optimistic local removal already done; rollback on failure
          for (const deleted of structuralDiff?.deletes ?? []) {
            const serviceId = deleted._id;
            if (!isDeletableServiceId(serviceId)) continue;
            const dropFromLocalBookkeeping = () => {
              dirtyServicesRef.current.delete(serviceId!);
              forgetServiceUpdatedAt(serviceId);
              lastSavedServicesRef.current =
                lastSavedServicesRef.current.filter((s) => s._id !== serviceId);
              didStructural = true;
            };
            try {
              const deleteTotals = await BudgetService.deleteBudgetServiceV2(
                budgetId,
                serviceId!,
                {
                  ifMatch: buildServiceIfMatch(
                    serviceUpdatedAtByIdRef.current.get(serviceId!)
                  ),
                  includeTotals,
                }
              );
              if (includeTotals && deleteTotals) {
                lastTotalsWriteResponse = deleteTotals;
              }
              dropFromLocalBookkeeping();
            } catch (deleteError) {
              const failure = classifyServiceDeleteFailure(deleteError);

              if (failure === "already-deleted") {
                dropFromLocalBookkeeping();
                continue;
              }

              if (failure === "rewrite-whole-document") {
                // Escala para o PUT do documento inteiro em vez de restaurar a
                // linha: restaurar faz a exclusão voltar a bater com a baseline,
                // o diff nunca mais a propõe, e o PUT de configuração seguinte
                // grava o fornecedor removido deixando as linhas órfãs. O PUT
                // completo grava array + configuração juntos, do estado local.
                console.warn(
                  "[Budget][Autosave] DELETE da linha falhou; gravando o documento inteiro para não separar a exclusão da configuração",
                  { budgetId, serviceId, deleteError }
                );
                needsFullDocumentPutRef.current = true;
                needsServicesRewriteRef.current = true;
                break;
              }

              activeBudgetDispatch({
                type: "RESTORE_SERVICE",
                payload: deleted,
              });
              const current = latestActiveBudgetRef.current;
              if (
                current &&
                !current.budgetServices?.some((s) => s._id === serviceId)
              ) {
                latestActiveBudgetRef.current = {
                  ...current,
                  budgetServices: [...(current.budgetServices || []), deleted],
                };
              }
              throw deleteError;
            }
          }

          const dirtyAfterStructural = dirtyServicesRef.current.size > 0;
          if (dirtyAfterStructural) {
            const phaseInfo = versionInfoRef.current;
            // Skip ops for ids that no longer exist locally (deleted mid-flight).
            const localIds = new Set(
              (latestActiveBudgetRef.current.budgetServices || [])
                .map((s) => s._id)
                .filter(Boolean) as string[]
            );
            for (const serviceId of Array.from(
              dirtyServicesRef.current.keys()
            )) {
              if (!localIds.has(serviceId)) {
                dirtyServicesRef.current.delete(serviceId);
              }
            }
            // Re-read phase values from the recalculated budget. Dispatch-time
            // snapshots can still be stale if flush races the React commit.
            const dirtySnapshot = refreshDirtyPhaseValuesFromBudget(
              dirtyServicesRef.current,
              latestActiveBudgetRef.current.budgetServices,
              phaseInfo
            );
            if (dirtySnapshot.size > 0) {
              const operations = buildBatchOperations(
                dirtySnapshot,
                phaseInfo,
                serviceUpdatedAtByIdRef.current
              );
              const chunks = chunkBatchOperations(operations);

              for (const chunk of chunks) {
                const batchResult =
                  await BudgetService.patchBudgetServicesBatchV2(
                    budgetId,
                    { operations: chunk },
                    { includeTotals }
                  );
                if (includeTotals) {
                  lastTotalsWriteResponse = batchResult;
                }
                for (const result of batchResult?.results ?? []) {
                  rememberServiceUpdatedAt(
                    result.serviceId,
                    result.updatedAt ?? result.service?.updatedAt
                  );
                }
              }

              Array.from(dirtySnapshot.entries()).forEach(
                ([serviceId, flushedValues]) => {
                  if (
                    dirtyServicesRef.current.get(serviceId) === flushedValues
                  ) {
                    dirtyServicesRef.current.delete(serviceId);
                  }
                }
              );
              didBatch = true;
            }
          }

          const budgetAfterStructural = latestActiveBudgetRef.current;
          const needsFullPutNow = needsFullDocumentPutRef.current;
          let didDocumentPut = false;

          if (needsFullPutNow) {
            const localBeforePut = budgetAfterStructural;
            const updatedFromApi = await BudgetService.update(
              budgetAfterStructural._id!,
              withAuditInfo(budgetAfterStructural)
            );
            const rawSaved =
              updatedFromApi === "" ? budgetAfterStructural : updatedFromApi;
            // PUT strips client totals (USE_SERVER_TOTALS); keep optimistic
            // trees until syncServerTotalsAfterSave applies GET /totals.
            const savedBudget =
              featureFlags.useServerTotals &&
              typeof rawSaved === "object" &&
              rawSaved?._id
                ? preserveLocalTotalsTrees(rawSaved, localBeforePut)
                : rawSaved;

            // Só limpa a INTENÇÃO de escrita quando nada mudou enquanto o PUT
            // estava em voo. Uma ação estrutural disparada durante o request
            // (duplicar logo depois de arrastar, por exemplo) marca esses refs
            // de novo; zerá-los aqui apagava a intenção dela, o flush
            // enfileirado não achava nada para enviar e a alteração ficava só
            // na tela — "Todas as alterações foram salvas" e sumia no reload.
            //
            // Mesmo critério que o `NEW_ACTIVE_BUDGET` logo abaixo já usa para
            // não sobrescrever edição feita durante o request.
            const noLocalEditDuringPut =
              localChangeRevisionRef.current === sentRevision;
            if (noLocalEditDuringPut) {
              needsFullDocumentPutRef.current = false;
              needsConfigPutRef.current = false;
            }
            didDocumentPut = true;
            // O PUT gravou o array inteiro: a baseline do diff estrutural
            // precisa acompanhar, senão o próximo flush veria as linhas
            // reescritas como "moves" e voltaria ao delete+create.
            //
            // A baseline acompanha SEMPRE (é o que o servidor tem agora), mas o
            // flag só cai junto com os demais: uma reescrita pedida durante o
            // PUT ainda precisa acontecer.
            if (needsServicesRewriteRef.current) {
              if (noLocalEditDuringPut) {
                needsServicesRewriteRef.current = false;
              }
              pinLastSavedServices(
                (typeof savedBudget === "object" &&
                Array.isArray(savedBudget?.budgetServices)
                  ? savedBudget.budgetServices
                  : budgetAfterStructural.budgetServices) as ServiceData[]
              );
            }

            budgetListRDispatch({
              type: "UPDATE_LIST",
              payload: budgetList.map((el) =>
                el._id === savedBudget._id
                  ? toBudgetListItem(
                      savedBudget,
                      el,
                      featureFlags.useV2BudgetList
                    )
                  : el
              ),
            });
            patchBudgetInListCache(
              queryClient,
              projectId,
              savedBudget._id!,
              typeof savedBudget === "object" && savedBudget?.budgetServices
                ? savedBudget
                : budgetAfterStructural
            );
            setBudgetDetailCache(
              queryClient,
              typeof savedBudget === "object" &&
                Array.isArray(savedBudget?.budgetServices)
                ? savedBudget
                : budgetAfterStructural
            );

            if (
              latestActiveBudgetRef.current?._id ===
                budgetAfterStructural._id &&
              localChangeRevisionRef.current === sentRevision &&
              typeof savedBudget === "object" &&
              savedBudget?._id
            ) {
              const normalized = mergeEditableServiceMetadataFromLocal(
                savedBudget,
                latestActiveBudgetRef.current
              );
              activeBudgetDispatch({
                type: "NEW_ACTIVE_BUDGET",
                payload: { ...normalized, isApiUpdate: true },
              });
            }
          } else if (didBatch || didStructural || needsConfigPutRef.current) {
            // FE-P3-03 slim PUT: sync config (+ client totals when flag off).
            // FE-P4-02: with USE_SERVER_TOTALS, batch/structural already persist
            // totals via includeTotals — skip slim PUT unless config dirty.
            // BudgetService.update strips totals trees when the flag is on.
            const needsConfig = needsConfigPutRef.current;
            const shouldSlimPut = needsConfig || !featureFlags.useServerTotals;

            if (shouldSlimPut) {
              const slimPayload = stripBudgetServicesForSlimPut(
                budgetAfterStructural
              );
              await BudgetService.update(
                budgetAfterStructural._id!,
                withAuditInfo(slimPayload)
              );
              // Mesma regra do PUT completo acima: intenção marcada durante o
              // request precisa sobreviver ao reset.
              if (localChangeRevisionRef.current === sentRevision) {
                needsConfigPutRef.current = false;
              }
              didDocumentPut = true;

              budgetListRDispatch({
                type: "UPDATE_LIST",
                payload: budgetList.map((el) =>
                  el._id === budgetAfterStructural._id
                    ? toBudgetListItem(
                        budgetAfterStructural,
                        el,
                        featureFlags.useV2BudgetList
                      )
                    : el
                ),
              });
              patchBudgetInListCache(
                queryClient,
                projectId,
                budgetAfterStructural._id!,
                budgetAfterStructural
              );
              setBudgetDetailCache(queryClient, budgetAfterStructural);
            }
          }

          // FE-P4-01: after flush completes, apply write-response totals slices
          // (or refetch after v1 PUT when totalsVersion may have advanced).
          // Runs only once dirty/structural work is done so NEW_ACTIVE_BUDGET
          // does not clear in-flight v2 write state mid-flush.
          if (includeTotals && (didBatch || didStructural || didDocumentPut)) {
            await syncServerTotalsAfterSave(
              budgetId,
              didDocumentPut ? undefined : lastTotalsWriteResponse,
              sentRevision,
              { forceRefetch: didDocumentPut }
            );
          }

          const flushEsvaziouAFila =
            dirtyServicesRef.current.size === 0 &&
            !needsFullDocumentPutRef.current &&
            !needsConfigPutRef.current;

          if (flushEsvaziouAFila) {
            markBudgetSaved(sentRevision);
          } else {
            hasPendingChangesRef.current = true;
            setHasPendingBudgetChanges(true);
            if (lastStatusRef.current !== "unsaved") {
              lastStatusRef.current = "unsaved";
              setBudgetAutosaveStatus("unsaved");
            }
          }

          // "Todas as alterações foram salvas" é disparado por ESTE contador
          // (ver Project/index.tsx). Ele só pode subir quando a fila esvaziou:
          // no ramo de cima o próprio código acabou de concluir que AINDA HÁ
          // trabalho pendente e marcou o orçamento como `unsaved` — anunciar
          // sucesso ali dizia ao usuário que estava tudo gravado enquanto o
          // status na tela dizia o contrário. Foi o retorno do QA: "o autosave
          // mostra que foram salvas; só quando você salva manualmente é que
          // ele dá o erro".
          if (flushEsvaziouAFila) {
            setBudgetAutosaveSuccessCount((prev) => prev + 1);
          }
          saveSucceeded = flushEsvaziouAFila;
        } else {
          // --- Legacy full-document v1 PUT (flag off, or no v2 dirty state) ---
          const updatedFromApi = await BudgetService.update(
            budgetToSave._id,
            withAuditInfo(budgetToSave)
          );

          const rawSaved =
            updatedFromApi === "" ? budgetToSave : updatedFromApi;
          const savedBudget =
            featureFlags.useServerTotals &&
            typeof rawSaved === "object" &&
            rawSaved?._id
              ? preserveLocalTotalsTrees(rawSaved, budgetToSave)
              : rawSaved;
          // Usa o estado local enviado como referência de "salvo" para evitar
          // loops quando a API devolve campos auxiliares diferentes.
          markBudgetSaved(sentRevision);

          budgetListRDispatch({
            type: "UPDATE_LIST",
            payload: budgetList.map((el) =>
              el._id === savedBudget._id
                ? toBudgetListItem(
                    savedBudget,
                    el,
                    featureFlags.useV2BudgetList
                  )
                : el
            ),
          });
          patchBudgetInListCache(
            queryClient,
            projectId,
            savedBudget._id!,
            savedBudget
          );
          setBudgetDetailCache(queryClient, savedBudget);

          // Só aplica a resposta da API se nenhuma edição foi feita desde que o
          // save foi iniciado. Se o usuário editou durante o voo, o contexto já
          // tem os valores corretos — sobrescrever causaria perda dos dados.
          if (
            latestActiveBudgetRef.current?._id === savedBudget?._id &&
            localChangeRevisionRef.current === sentRevision
          ) {
            const normalized = mergeEditableServiceMetadataFromLocal(
              savedBudget,
              latestActiveBudgetRef.current
            );

            activeBudgetDispatch({
              type: "NEW_ACTIVE_BUDGET",
              payload: { ...normalized, isApiUpdate: true },
            });
          }

          // FE-P4-01: after full PUT, refresh server totals when flag on.
          if (featureFlags.useServerTotals && savedBudget?._id) {
            await syncServerTotalsAfterSave(
              savedBudget._id,
              undefined,
              sentRevision,
              { forceRefetch: true }
            );
          }

          setBudgetAutosaveSuccessCount((prev) => prev + 1);
          saveSucceeded = true;
        }
      } catch (error: any) {
        const status = error?.response?.status;
        const data = error?.response?.data;
        const message =
          (data && (data.message || data.error)) || error?.message || "unknown";

        if (status === 403 && tabActive === TabActive.APPROVED) {
          showNotification(
            data?.message ||
              "A guia de Fechamento está bloqueada para edição no status atual.",
            "warning"
          );
          void refetchClosingControl();
        }

        // Keep local dirty values and hold automatic flushes. Refreshing
        // If-Match here would let the 60s interval overwrite the other editor.
        if (
          featureFlags.useV2ServiceWrites &&
          isServiceVersionConflictError(error) &&
          budgetToSave?._id
        ) {
          serviceVersionConflictHoldRef.current = true;
          toast.warn(
            "Esta linha foi alterada em outra sessão. Suas edições locais foram mantidas — salve novamente para reaplicar.",
            { toastId: "budget-service-version-conflict" }
          );
        }

        console.error("[Budget][Autosave] Falha ao salvar", {
          budgetId: budgetToSave?._id,
          source,
          status,
          message,
          data,
          useV2ServiceWrites: featureFlags.useV2ServiceWrites,
          versionConflict: isServiceVersionConflictError(error),
        });
        hasPendingChangesRef.current = true;
        setHasPendingBudgetChanges(true);
        setHasBudgetAutosaveFailure(true);
        setBudgetAutosaveStatus("error");
        if (!isServiceVersionConflictError(error)) {
          toast.error("Houve um problema ao salvar as alterações", {
            toastId: "budget-autosave-error",
          });
        }
      } finally {
        isAutosaveRunningRef.current = false;
        releaseAddServiceFlushHolds(addServiceFlushEpoch);
      }

      if (shouldRunQueuedSaveRef.current) {
        shouldRunQueuedSaveRef.current = false;
        if (
          (hasRealChanges(latestActiveBudgetRef.current) ||
            dirtyServicesRef.current.size > 0 ||
            needsFullDocumentPutRef.current ||
            needsConfigPutRef.current) &&
          isBudgetAutosaveTab &&
          !isBudgetDataModalOpenRef.current
        ) {
          void runBudgetAutosave("interval");
        }
      }

      return saveSucceeded;
    },
    [
      activeBudgetDispatch,
      budgetList,
      forgetServiceUpdatedAt,
      hasBudgetAutosaveFailure,
      hasRealChanges,
      isBudgetAutosaveTab,
      markBudgetSaved,
      projectId,
      queryClient,
      refetchClosingControl,
      rememberServiceUpdatedAt,
      resetV2ServiceWriteState,
      syncServerTotalsAfterSave,
      tabActive,
    ]
  );

  useEffect(() => {
    runBudgetAutosaveRef.current = runBudgetAutosave;
  }, [runBudgetAutosave]);

  useEffect(() => {
    latestActiveBudgetRef.current = activeBudget;
  }, [activeBudget]);

  // Reseta o aviso de tipo do orçamento quando um tipo válido é selecionado ou
  // quando o orçamento ativo muda.
  useEffect(() => {
    if (activeBudget?.type) {
      budgetTypeWarningShownRef.current = false;
      return;
    }
    if (!activeBudget?._id) {
      budgetTypeWarningShownRef.current = false;
    }
  }, [activeBudget?.type, activeBudget?._id]);

  // Baseline de "salvo" só ao carregar um orçamento novo da API — não a cada
  // sync pós-autosave (isApiUpdate permanece no objeto e totais mudam a cada
  // UPDATE_CALC_VALUES; chamar markBudgetSaved nesses casos zerava o dirty).
  useEffect(() => {
    if (!activeBudget?.isApiUpdate || !activeBudget?._id) {
      return;
    }

    const isDifferentBudget =
      lastLoadedBudgetIdRef.current !== activeBudget._id;
    const isReload = lastLoadedBudgetIdRef.current === null;

    if (!isDifferentBudget && !isReload) {
      return;
    }

    console.debug("[Budget] Novo orçamento carregado:", activeBudget._id);
    lastLoadedBudgetIdRef.current = activeBudget._id;
    initialLoadCompleteRef.current = false;
    markBudgetSaved();
  }, [activeBudget?._id, activeBudget?.isApiUpdate, markBudgetSaved]);

  // MMV-2626-3: o recálculo da abertura mudou linhas/totais em relação ao
  // gravado. Marca como alteração local para o autosave (ou o "salvar antes
  // de exportar") gravar o documento que a tela mostra — senão a lista de
  // orçamentos e o xlsx seguiam lendo os totais velhos até alguém clicar em
  // Salvar. Orçamento somente-leitura não grava (`runBudgetAutosave` sai
  // antes), então a marcação é inofensiva ali.
  useEffect(() => {
    const pendingId = pendingHydrateRebuildSaveRef.current;
    if (!pendingId || activeBudget?._id !== pendingId) return;
    pendingHydrateRebuildSaveRef.current = null;

    localChangeRevisionRef.current += 1;
    hasPendingChangesRef.current = true;
    setHasPendingBudgetChanges(true);
    if (featureFlags.useV2ServiceWrites && !featureFlags.useServerTotals) {
      // Linhas e árvores mudaram só em memória e já estão "fixadas" como
      // salvas no diff v2 — só o PUT do documento inteiro as grava.
      // Com totais no servidor esse PUT é descartado e recalculado, então
      // não entra na fila: a tela segue com o overlay até a árvore bater.
      needsFullDocumentPutRef.current = true;
    }
    if (lastStatusRef.current !== "unsaved") {
      lastStatusRef.current = "unsaved";
      setBudgetAutosaveStatus("unsaved");
    }
  }, [activeBudget]);

  // Libera autosave automático quando a estrutura inicial estiver pronta.
  useEffect(() => {
    if (!activeBudget?._id || initialLoadCompleteRef.current) {
      return;
    }

    if (loadCompleteTimeoutRef.current) {
      cancelAnimationFrame(loadCompleteTimeoutRef.current);
      clearTimeout(loadCompleteTimeoutRef.current);
      loadCompleteTimeoutRef.current = null;
    }

    const releaseAutosave = () => {
      initialLoadCompleteRef.current = true;
      loadCompleteTimeoutRef.current = null;
    };

    if (isBudgetFullyLoaded(activeBudget)) {
      loadCompleteTimeoutRef.current = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const loadTime = Date.now() - loadStartTimeRef.current;
          console.debug(`[Budget] Carregamento completo em ${loadTime}ms`);
          releaseAutosave();
        });
      }) as unknown as number;
      return;
    }

    loadCompleteTimeoutRef.current = window.setTimeout(() => {
      if (!initialLoadCompleteRef.current) {
        const loadTime = Date.now() - loadStartTimeRef.current;
        console.warn(
          `[Budget] Timeout de carregamento (${loadTime}ms) - liberando autosave`
        );
        releaseAutosave();
      }
    }, 2000) as unknown as number;
  }, [
    activeBudget?._id,
    activeBudget?.budgetValuesTotal,
    activeBudget?.budgetServices?.length,
  ]);

  // Cleanup da animação/timeout de carregamento no unmount
  useEffect(() => {
    return () => {
      if (loadCompleteTimeoutRef.current) {
        // Tenta cancelar ambos (um deles não fará nada se não for o tipo correto)
        cancelAnimationFrame(loadCompleteTimeoutRef.current);
        clearTimeout(loadCompleteTimeoutRef.current);
        loadCompleteTimeoutRef.current = null;
      }
      clearServiceWritesDebounce();
    };
  }, [clearServiceWritesDebounce]);

  useEffect(() => {
    if (!activeBudget?._id) {
      return;
    }

    const hasChanges = hasRealChanges(activeBudget);
    hasPendingChangesRef.current = hasChanges;
    setHasPendingBudgetChanges(hasChanges);

    if (!hasChanges) {
      if (!isAutosaveRunningRef.current && !hasBudgetAutosaveFailure) {
        const newStatus: typeof lastStatusRef.current = "saved";
        if (lastStatusRef.current !== newStatus) {
          lastStatusRef.current = newStatus;
          setBudgetAutosaveStatus(newStatus);
        }
      }
      return;
    }

    if (!isAutosaveRunningRef.current) {
      const newStatus: typeof lastStatusRef.current = "unsaved";
      if (lastStatusRef.current !== newStatus) {
        lastStatusRef.current = newStatus;
        setBudgetAutosaveStatus(newStatus);
      }
    }
  }, [activeBudget, hasRealChanges, hasBudgetAutosaveFailure]);

  // Autosave periódico: a cada 30s, persiste se houver alterações locais.
  useEffect(() => {
    if (!isBudgetAutosaveTab || !activeBudget?._id) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!initialLoadCompleteRef.current) {
        return;
      }
      if (isBudgetDataModalOpenRef.current) {
        return;
      }
      if (isAutosaveRunningRef.current) {
        return;
      }
      if (isBudgetDisabledRef.current) {
        return;
      }
      if (serviceVersionConflictHoldRef.current) {
        return;
      }

      const budget = latestActiveBudgetRef.current;
      if (!budget?._id) {
        return;
      }

      if (localChangeRevisionRef.current === lastSavedRevisionRef.current) {
        return;
      }

      void runBudgetAutosaveRef.current("interval");
    }, BUDGET_AUTOSAVE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isBudgetAutosaveTab, activeBudget?._id]);

  async function createEmptyBudget(type: BudgetType) {
    if (!!projectId)
      await createBudget({
        project: projectId,
        type,
        status: "OPEN",
        budgetServices: [],
        paymentSolicitation: [],
        itemTypeList: {} as ItemTypeListPerCalcOption,
        supplierList: [],
        addedItemTypeList: [],
        budgetValuesTotal: {
          byCalculationType: {} as AllTotals,
          byItemType: {} as ByItemType,
        },
        negociationValuesTotal: [] as VersionTotals[],
        closingValuesTotal: {} as VersionTotals,
        negotiationVersion: 0,
        files: [],
        versionHistory: [{ status: "OPEN", version: 0, comparisonVersion: 0 }],
        budgetIndex: 0,
        generalInfo: [
          {
            definition: "",
            rfp: false,
            tagGMP: "",
            local: "",
            city: "",
            country: "",
            state: "",
            address: "",
            checkIn: "",
            checkOut: "",
            meetingPeriod: "",
            availability: "",
            deadline: "",
            site: "",
            // version: 0,
          },
        ],
        baseValues: [],
        mainAppliedTaxes: [],
        budgetWideAppliedTaxes: [],
      });
  }

  // Ações de status (aprovar, negociar, nova versão, reprovar) trocam o
  // orçamento ativo pelo documento local antes da resposta da API. Os caches do
  // react-query precisam acompanhar: com a lista v2, reabrir o editor (ex.:
  // voltar de Resumo/Serviços) mescla o item da lista com o detalhe em cache, e
  // um detalhe de antes da aprovação trazia o Fechamento em branco — que o
  // recálculo de totais marcava como alterado e o autosave gravava (MMV-2665).
  function syncBudgetCachesWithLocal(budget: Budget) {
    if (!budget?._id) return;
    setBudgetDetailCache(queryClient, budget);
    patchBudgetInListCache(queryClient, projectId, budget._id, budget);
  }

  async function updateBudget(
    budgetId: string,
    budgetData: Budget,
    action: string,
    version: number
  ) {
    try {
      const versInfo = budgetData.versionHistory.find(
        (el) => el.version === version
      );
      //check negotiations (inclui DENIED para manter aba visível como histórico)
      const negotitations = budgetList.find(
        (el) =>
          !!el.versionHistory.find(
            (ver: any) => ver.status === "NEGOTIATE" || ver.status === "DENIED"
          ) && el.status !== "DELETED"
      );
      if (!negotitations) {
        setHasNegotiations(false);
      }
      // check closings (inclui DENIED_CLOSING para manter aba visível como histórico)
      const closing = budgetList.find(
        (el) =>
          !!el.versionHistory.find(
            (ver: any) =>
              ver.status === "APPROVED" || ver.status === "DENIED_CLOSING"
          ) && el.status !== "DELETED"
      );
      if (!closing) {
        setHasClosings(false);
      }
      if (budgetId) {
        // Adiciona o actionType aos dados do orçamento para auditoria.
        // `auditStage` diz ao backend em qual bloco de valores o diff de
        // auditoria deve ser calculado: o status do orçamento não revela onde a
        // edição aconteceu (um orçamento aberto é editável na aba de
        // Fechamento, gravando em `closingValues`), e sem essa informação o
        // campo editado some do registro de auditoria.
        const budgetDataWithAction = {
          ...budgetData,
          actionType: action,
          auditStage: auditStageFromTab(tabActive),
        };

        if (action === "SAVE") {
          // Captura a revisão local antes do request para resolver corridas
          // se o usuário continuar editando enquanto o save está em voo.
          const sentRevision = localChangeRevisionRef.current;
          const updatedFromApi = await BudgetService.update(
            budgetId,
            budgetDataWithAction
          );
          const rawSaved = updatedFromApi === "" ? budgetData : updatedFromApi;
          // PUT strips client totals when USE_SERVER_TOTALS; keep optimistic
          // trees until GET /totals applies the server slice.
          const savedBudget =
            featureFlags.useServerTotals &&
            typeof rawSaved === "object" &&
            rawSaved?._id
              ? preserveLocalTotalsTrees(rawSaved, budgetData)
              : rawSaved;

          // Idem ao autosave: edição feita durante o request mantém a intenção
          // de escrita viva, senão ela se perde entre o save e o próximo flush.
          if (localChangeRevisionRef.current === sentRevision) {
            needsFullDocumentPutRef.current = false;
            needsConfigPutRef.current = false;
          }
          markBudgetSaved(sentRevision);

          // Mantém o orçamento ativo alinhado ao que foi salvo; caso contrário o autosave
          // pode reenviar estado antigo e apagar dados (ex.: generalInfo de outro save).
          if (activeBudget?._id === savedBudget?._id) {
            activeBudgetDispatch({
              type: "NEW_ACTIVE_BUDGET",
              payload: { ...savedBudget, isApiUpdate: true },
            });
          }

          // Atualiza budgetList localmente para refletir as mudanças imediatamente
          budgetListRDispatch({
            type: "UPDATE_LIST",
            payload: budgetList.map((el) =>
              el._id === savedBudget._id
                ? toBudgetListItem(
                    savedBudget,
                    el,
                    featureFlags.useV2BudgetList
                  )
                : el
            ),
          });
          if (savedBudget?._id) {
            patchBudgetInListCache(
              queryClient,
              projectId,
              savedBudget._id,
              savedBudget
            );
            setBudgetDetailCache(queryClient, savedBudget);
          }

          if (featureFlags.useServerTotals && budgetId) {
            await syncServerTotalsAfterSave(budgetId, undefined, sentRevision, {
              forceRefetch: true,
            });
          }

          return updatedFromApi;
        }
        if (action === "DELETED") {
          activeBudgetDispatch({
            type: "NO_ACTIVE_BUDGET",
            payload: undefined,
          });
          setDetailedBudget("");
          setVersionInfo({
            comparisonVersion: 0,
            version: 0,
            status: "",
          });

          if (tabActive === TabActive.APPROVED) {
            setHasClosings(false);
            setTabActive(TabActive.BUDGET);
          }

          return await BudgetService.update(budgetId, budgetDataWithAction);
        }
        if (action === "DENIED") {
          const openNegotiations = budgetData.versionHistory.filter(
            (el) => el.status === "NEGOTIATE"
          );
          const deniedNegotiations = budgetData.versionHistory.filter(
            (el) => el.status === "DENIED"
          );

          // Atualiza activeBudget e budgetList com o novo estado (versão marcada como DENIED)
          activeBudgetDispatch({
            type: "NEW_ACTIVE_BUDGET",
            payload: budgetData,
          });
          syncBudgetCachesWithLocal(budgetData);
          budgetListRDispatch({
            type: "UPDATE_LIST",
            payload: budgetList.map((el) =>
              el._id === budgetData._id
                ? toBudgetListItem(budgetData, el, featureFlags.useV2BudgetList)
                : el
            ),
          });

          if (openNegotiations.length === 0 && deniedNegotiations.length > 0) {
            // Mantém na aba de negociação apontando para a última versão reprovada (read-only)
            const lastDenied =
              deniedNegotiations[deniedNegotiations.length - 1];
            setTabActive(TabActive.NEGOTIATION);
            setDetailedBudget(
              `${budgetData?._id}-version-${lastDenied.version}`
            );
            setVersionInfo(lastDenied);
            // Mantém hasNegotiations = true para a aba permanecer visível para histórico
          } else if (openNegotiations.length > 0) {
            setTabActive(TabActive.NEGOTIATION);
            setDetailedBudget(
              `${budgetData?._id}-version-${openNegotiations[0].version ?? 0}`
            );
            setVersionInfo(openNegotiations[0]);
          }

          return await BudgetService.update(budgetId, budgetDataWithAction);
        }
        if (action === "DENIED_CLOSING") {
          // Mantém a aba de fechamento visível com a versão reprovada em read-only
          setHasClosings(true);
          if (versInfo) setVersionInfo(versInfo);
          activeBudgetDispatch({
            type: "NEW_ACTIVE_BUDGET",
            payload: budgetData,
          });
          syncBudgetCachesWithLocal(budgetData);
          budgetListRDispatch({
            type: "UPDATE_LIST",
            payload: budgetList.map((el) =>
              el._id === budgetData._id
                ? toBudgetListItem(budgetData, el, featureFlags.useV2BudgetList)
                : el
            ),
          });
          return await BudgetService.update(budgetId, budgetDataWithAction);
        }
        if (action === "APPROVED") {
          beginClosingApprovalTransition();
          setTabActive(TabActive.APPROVED);
          setNegotiationVersion(budgetData.negotiationVersion);
          setDetailedBudget(
            `${budgetData?._id}-version-${version ? version : 0}`
          );
          setHasClosings(true);

          // Atualizar budgetList para refletir o fechamento
          budgetListRDispatch({
            type: "UPDATE_LIST",
            payload: budgetList.map((el) =>
              el._id === budgetData._id
                ? toBudgetListItem(budgetData, el, featureFlags.useV2BudgetList)
                : el
            ),
          });
        }
        if (action === "NEGOTIATE") {
          setTabActive(TabActive.NEGOTIATION);
          setNegotiationVersion(budgetData.negotiationVersion);
          setDetailedBudget(budgetData?._id + "-version-" + version);
          setHasNegotiations(true);
        }
        if (action === "NEW_VERSION") {
          setDetailedBudget(budgetData?._id + "-version-" + version);
          // Verificar se a nova versão criada é uma negociação
          const newVersionIsNegotiation = budgetData.versionHistory?.some(
            (vh: any) => vh.version === version && vh.status === "NEGOTIATE"
          );
          if (newVersionIsNegotiation) {
            setHasNegotiations(true);
          }
        }
        if (versInfo) setVersionInfo(versInfo);
        activeBudgetDispatch({
          type: "NEW_ACTIVE_BUDGET",
          payload: budgetData,
        });
        syncBudgetCachesWithLocal(budgetData);
        // Atualizar budgetList localmente para refletir as mudanças
        budgetListRDispatch({
          type: "UPDATE_LIST",
          payload: budgetList.map((el) =>
            el._id === budgetData._id
              ? toBudgetListItem(budgetData, el, featureFlags.useV2BudgetList)
              : el
          ),
        });

        const updateResult = await BudgetService.update(
          budgetId,
          budgetDataWithAction
        );

        if (action === "APPROVED") {
          endClosingApprovalTransition();
          if (closingControlProjectId && budgetId) {
            // Busca o estado de fechamento e grava direto no cache. Evita
            // depender do refetch do observer, que pode não propagar quando a
            // query acabou de ser reabilitada na troca de aba (react-query v3)
            // — sintoma: o botão "Pré Fechamento" só aparecia após dar refresh.
            try {
              const closingState = await ClosingControlService.get(
                closingControlProjectId,
                budgetId.toString()
              );
              applyClosingControlState(closingState);
            } catch {
              void refetchClosingControl();
            }
          }
        }

        return updateResult;
      }
    } catch (error: any) {
      if (action === "APPROVED") {
        endClosingApprovalTransition();
      }

      const isClosingBlocked =
        error?.response?.status === 403 &&
        tabActive === TabActive.APPROVED &&
        (action === "SAVE" ||
          error?.response?.data?.message?.includes?.("bloqueada para edição"));

      if (isClosingBlocked) {
        showNotification(
          error?.response?.data?.message ||
            "A guia de Fechamento está bloqueada para edição no status atual.",
          "warning"
        );
        void refetchClosingControl();
        return null;
      }

      showNotification(`Não foi possível atualizar o orçamento`, "warning");
      return null;
    }
  }

  function showNotification(message: string, severity = "success") {
    setNotification({ open: true, message, severity });
  }

  function closeNotification() {
    setNotification({ ...notification, open: false });
  }

  async function loadBudgetList(projectId: string) {
    setLoadingBudget(true);
    loadStartTimeRef.current = Date.now();
    // Reseta flags ao iniciar novo carregamento (primeira entrada ou retorno à tela)
    initialLoadCompleteRef.current = false;
    lastLoadedBudgetIdRef.current = null;

    try {
      setProjectId(projectId);
      // FE-P1-01: flagged v2 summary list vs legacy full-document list.
      // When useV2BudgetList is on, rows are BudgetSummary[] (no budgetServices).
      const rows: BudgetListItem[] = featureFlags.useV2BudgetList
        ? await fetchBudgetSummaryList(queryClient, projectId)
        : await fetchBudgetList(queryClient, projectId);
      if (rows.length > 0) {
        const activeBudgets = rows.filter((el) => el.status !== "DELETED");
        // Tab visibility derives from versionHistory on list rows (works for
        // both full Budget and BudgetSummary).
        const negotiations = rows.find(
          (el) =>
            !!el.versionHistory?.find(
              (ver) => ver.status === "NEGOTIATE" || ver.status === "DENIED"
            )
        );

        const closing = rows.find(
          (el) =>
            !!el.versionHistory?.find(
              (ver) =>
                ver.status === "APPROVED" || ver.status === "DENIED_CLOSING"
            )
        );
        setHasClosings(!!closing);
        setHasNegotiations(!!negotiations);
        if (activeBudgets.length > 0)
          budgetListRDispatch({ type: "UPDATE_LIST", payload: activeBudgets });
      } else {
        budgetListRDispatch({ type: "UPDATE_LIST", payload: [] });
        activeBudgetDispatch({ payload: undefined, type: "NO_ACTIVE_BUDGET" });
        setHasClosings(false);
        setHasNegotiations(false);
      }
    } catch (error) {
      showNotification("Não foi possível carregar", "warning");
    }
    setLoadingBudget(false);
  }

  /**
   * Commit a hydrated editor document, nesta ordem:
   * 1. refresh totals from BE when `USE_SERVER_TOTALS` is on (FE-P4-01);
   * 2. backfill do perfil fiscal da categoria que falta
   *    (`backfillMissingCategoryFiscalRows`): sem a linha de `baseValues` da
   *    versão, o recálculo cai no dummy zerado e zera Taxa MM/Imposto das
   *    linhas;
   * 3. rebuild phase trees with the same client overlay used while editing
   *    (category `baseValues.taxData` → lines). Server/list snapshots often
   *    omit that overlay, so without this step a refresh shows the old total
   *    until the user opens category Valores.
   * Se 2+3 mudaram os totais gravados, `hasHydrateRebuildDrift` agenda o
   * autosave, que persiste as linhas novas de `baseValues` junto.
   */
  async function commitHydratedBudget(
    budget: Budget,
    stageVersion?: versionInfo
  ): Promise<void> {
    let next = budget;
    // `stageVersion` é a aba que o clique abriu. O ref ainda pode estar no
    // Orçamento: v0 existe em todo documento, então o fallback reconstruiria
    // a árvore errada e o Fechamento pintaria R$ 0,00 com a sidebar no total
    // real (ela soma `closingValues`, não a árvore).
    const hydrateVersionInfo = versionInfoForTotalsRebuild(
      resolveHydrateVersionInfo(budget, stageVersion ?? versionInfoRef.current)
    );

    if (featureFlags.useServerTotals && budget._id) {
      try {
        const phaseParams =
          resolveTotalsPhaseFromVersionInfo(hydrateVersionInfo);
        const totalsResponse = await fetchBudgetTotals(
          queryClient,
          budget._id,
          phaseParams
        );
        next = applyHydrateServerTotals(
          budget,
          totalsResponseToAttachment(totalsResponse)
        );
      } catch (error) {
        console.error("[Budget][ServerTotals] Hydrate totals refresh failed", {
          budgetId: budget._id,
          error,
        });
      }
    }

    const { beforeRebuild, rebuilt } = backfillThenRebuildHydratedBudget(
      next,
      (backfilled) =>
        rebuildTotalsWithTaxOverlay(backfilled, hydrateVersionInfo)
    );
    next = rebuilt;
    pendingHydrateRebuildSaveRef.current =
      next._id &&
      hasHydrateRebuildDrift(beforeRebuild, next, hydrateVersionInfo)
        ? next._id
        : null;

    const withFlag = { ...next, isApiUpdate: true as const };

    setBudgetDetailCache(queryClient, withFlag);
    if (withFlag._id) {
      patchBudgetInListCache(queryClient, projectId, withFlag._id, withFlag);
      budgetListRDispatch({
        type: "UPDATE_LIST",
        payload: budgetList.map((el) =>
          el._id === withFlag._id
            ? toBudgetListItem(withFlag, el, featureFlags.useV2BudgetList)
            : el
        ),
      });
    }
    activeBudgetDispatch({
      type: "NEW_ACTIVE_BUDGET",
      payload: withFlag,
    });
  }

  /**
   * FE-P1-03 / FE-P3-01: set `activeBudget` from a sidebar/list row.
   * - Full `Budget` (v1 list, `useV2BudgetDetail` off): dispatch immediately.
   * - Summary or `useV2BudgetDetail` on: fetch detail → merge → dispatch.
   *   Detail source is v2 shell+services when the flag is on, else v1 GET.
   * Never dispatches a summary-only payload.
   * With `USE_SERVER_TOTALS`, always refreshes phase totals via GET /totals
   * before commit so the sidebar card matches the server tree.
   */
  async function hydrateActiveBudget(
    listItem: BudgetListItem,
    stageVersion?: versionInfo
  ): Promise<void> {
    const budgetId = listItem?._id;
    if (!budgetId) return;
    if (stageVersion) {
      versionInfoRef.current = stageVersion;
    }

    // Already hydrated as the active editor document — skip detail fetch.
    // Still refresh server totals when the flag is on (list row may be stale).
    // Troca de aba (Orçamento → Fechamento) também passa por aqui: o documento
    // já tem serviços, mas a árvore da etapa nova ainda não foi reconstruída.
    if (
      activeBudget?._id === budgetId &&
      Array.isArray(activeBudget.budgetServices)
    ) {
      if (featureFlags.useServerTotals || stageVersion) {
        await commitHydratedBudget(activeBudget, stageVersion);
      } else {
        setBudgetDetailCache(queryClient, activeBudget);
      }
      return;
    }

    // FE-P3-01: with v2 detail on, always bootstrap via shell + services
    // (do not short-circuit on full v1 list rows).
    if (!featureFlags.useV2BudgetDetail && isFullBudget(listItem)) {
      await commitHydratedBudget(listItem, stageVersion);
      return;
    }

    const gen = ++detailFetchGenRef.current;
    setIsLoadingBudgetDetail(true);
    try {
      const detail = await fetchBudgetDetail(queryClient, budgetId);
      if (gen !== detailFetchGenRef.current) return;

      const summaryOverlay = isFullBudget(listItem)
        ? budgetToSummary(listItem)
        : (listItem as BudgetSummary);
      const merged = mergeSummaryWithDetail(summaryOverlay, detail);
      if (gen !== detailFetchGenRef.current) return;
      await commitHydratedBudget(merged, stageVersion);
    } catch (error) {
      if (gen === detailFetchGenRef.current) {
        console.error("[Budget] Detail hydrate failed:", error);
        showNotification("Não foi possível carregar o orçamento", "warning");
      }
    } finally {
      if (gen === detailFetchGenRef.current) {
        setIsLoadingBudgetDetail(false);
      }
    }
  }

  async function createBudget(data: Budget) {
    setLoadingBudget(true);
    try {
      const budgetData = await BudgetService.create(data);
      // Marca como isApiUpdate para evitar auditoria duplicada de update após create
      const budgetDataWithFlag = { ...budgetData, isApiUpdate: true };
      showNotification("Criado com Sucesso!", "success");
      budgetListRDispatch({
        type: "UPDATE_LIST",
        payload: [
          ...budgetList,
          toBudgetListItem(
            budgetDataWithFlag,
            undefined,
            featureFlags.useV2BudgetList
          ),
        ],
      });
      activeBudgetDispatch({
        payload: budgetDataWithFlag,
        type: "NEW_ACTIVE_BUDGET",
      });
      setBudgetDetailCache(queryClient, budgetDataWithFlag);
      setNegotiationVersion(0);
      setVersionInfo({
        comparisonVersion: 0,
        version: 0,
        status: "OPEN",
      });
      // Define detailedBudget depois para que o useEffect veja isApiUpdate: true
      setDetailedBudget(budgetData._id + "-version-0");
    } catch (error) {
      showNotification("Não foi possível criar", "warning");
    } finally {
      setLoadingBudget(false);
    }
  }

  function clearBudget() {
    activeBudgetDispatch({ payload: undefined, type: "NO_ACTIVE_BUDGET" });
    setProjectId(undefined);
  }

  const temp = useRef<any>(null);
  /** ID temporário estável enquanto o orçamento ainda não foi salvo (ex.: duplicata local). */
  const tempBudgetIdRef = useRef<string | null>(null);

  const handleTempTaxData = (value: any, type: string) => {
    const taxTypeToAttributeMap: any = {
      SERVICE: "serviceTax",
      ISS: "iss",
      TARIFF: "tariff",
      LOCAL_TAX: "localTax",
      TOURISM_TAX: "tourismTax",
      FEE: "fee",
      IOF: "iof",
      MARKUP: "markup",
      OVER: "overhead",
      COMMISSION: "commission",
      INCENTIVE: "incentive",
      TRIBUTE: "tribute",
    };

    if (!temp.current) {
      temp.current = value;
    } else {
      const mapType = taxTypeToAttributeMap[type];
      const values = value?.budgetValues?.taxData?.[mapType];

      temp.current = {
        ...temp.current,
        budgetValues: {
          ...temp.current?.budgetValues,
          taxData: {
            ...temp.current?.budgetValues?.taxData,
            [mapType]: {
              ...temp.current?.budgetValues?.taxData?.[mapType],
              value: values.value,
              typeTax: values.typeTax,
            },
          },
        },
      };
    }
  };

  const cleanTempTaxData = () => (temp.current = null);

  const isBudgetDisabled = useMemo(() => {
    // Block editor while detail is loading or selected row is not yet hydrated.
    if (isLoadingBudgetDetail) return true;

    const detailedId = detailedBudget?.split("-")[0];
    if (
      detailedId &&
      (!activeBudget?._id ||
        activeBudget._id !== detailedId ||
        !Array.isArray(activeBudget.budgetServices))
    ) {
      // Only enforce hydration gate when the list may be summary-only.
      if (featureFlags.useV2BudgetList) return true;
    }

    const detailedVersion = detailedBudget.split("-");
    const version = Number(detailedVersion[2]);

    if (tabActive === TabActive.BUDGET) {
      // Se já existe negociação ou fechamento (inclusive reprovados), o orçamento original fica desabilitado
      return (
        activeBudget?.versionHistory?.some((i) => i.status === "NEGOTIATE") ||
        activeBudget?.versionHistory?.some((i) => i.status === "APPROVED") ||
        activeBudget?.versionHistory?.some((i) => i.status === "DENIED") ||
        activeBudget?.versionHistory?.some((i) => i.status === "DENIED_CLOSING")
      );
    }

    if (tabActive === TabActive.NEGOTIATION) {
      const isConsulting =
        activeBudget?.versionHistory?.find((i) => i.isConsulting)?.version ===
        version;

      // Versão de Assessoria: nunca desabilita por estas regras
      if (isConsulting) return false;

      // Última negociação regular (maior version não-consulting com status NEGOTIATE)
      const lastNegVersion =
        activeBudget?.versionHistory
          ?.filter((v) => v.status === "NEGOTIATE" && !v.isConsulting)
          ?.reduce((max, v) => (v.version > max ? v.version : max), 0) ?? 0;

      // Se a versão atual está reprovada, sempre desabilita (read-only para histórico)
      const currentVersionStatus = activeBudget?.versionHistory?.find(
        (v) => v.version === version
      )?.status;
      if (currentVersionStatus === "DENIED") return true;

      // Se lastNegVersion for 0 mas version > 0, a negociação está sendo criada agora
      // (versionHistory ainda não foi atualizado). Não desabilita para evitar aparência cinza.
      // Exceção: se já existem versões DENIED, a negociação foi reprovada — desabilita.
      if (lastNegVersion === 0 && version > 0) {
        const hasDeniedVersions = activeBudget?.versionHistory?.some(
          (v) => v.status === "DENIED"
        );
        if (!hasDeniedVersions) return false;
      }

      // Desabilita se não for a última negociação OU se já existir fechamento (APPROVED ou reprovado)
      return (
        version !== lastNegVersion ||
        (activeBudget?.versionHistory?.some(
          (i) => i.status === "APPROVED" || i.status === "DENIED_CLOSING"
        ) ??
          false)
      );
    }

    if (tabActive === TabActive.APPROVED) {
      const currentVersion = activeBudget?.versionHistory?.find(
        (entry) => entry.version === version
      );

      if (currentVersion?.status === "DENIED_CLOSING") {
        return true;
      }

      if (resolvedClosingControl != null) {
        return !resolvedClosingControl.canEditClosing;
      }

      if (isClosingTabActive && isLoadingClosingControl) {
        return true;
      }

      return isClosingTabActive;
    }

    return false;
  }, [
    tabActive,
    activeBudget,
    detailedBudget,
    isLoadingBudgetDetail,
    resolvedClosingControl,
    isClosingTabActive,
    isLoadingClosingControl,
  ]);

  useEffect(() => {
    isBudgetDisabledRef.current = isBudgetDisabled;
    if (isBudgetDisabled) {
      // Drop stale dirty while read-only (e.g. Orçamento after NEGOTIATE exists).
      markBudgetSaved();
    }
  }, [isBudgetDisabled, markBudgetSaved]);

  // -----------------------------------------------------------------------
  // Slice values. Each `useMemo` is wired to the smallest set of fields the
  // slice exposes, so a re-render of `BudgetProvider` only invalidates the
  // slices whose underlying data actually changed. The reducer does
  // `let updatedBudget = { ...state }` on every action, so unchanged nested
  // arrays/objects keep their references and slice memos hit even when
  // `activeBudget` itself is a new top-level reference.
  // -----------------------------------------------------------------------

  const metaValue = useMemo<BudgetMetaSlice>(
    () => ({
      activeBudgetId: activeBudget?._id,
      status: activeBudget?.status,
      type: activeBudget?.type,
      name: activeBudget?.name,
      generalInfo: activeBudget?.generalInfo,
      versionHistory: activeBudget?.versionHistory,
      budgetIndex: activeBudget?.budgetIndex,
      negotiationVersion,
      versionInfo,
      detailedBudget,
      activeCalcOption,
      activeBudgetIndex,
      isBudgetDisabled,
      loadingBudget,
      isLoadingBudgetDetail,
      isLoadingCalcs,
      hasNegotiations,
      hasClosings,
      projectId,
    }),
    [
      activeBudget?._id,
      activeBudget?.status,
      activeBudget?.type,
      activeBudget?.name,
      activeBudget?.generalInfo,
      activeBudget?.versionHistory,
      activeBudget?.budgetIndex,
      negotiationVersion,
      versionInfo,
      detailedBudget,
      activeCalcOption,
      activeBudgetIndex,
      isBudgetDisabled,
      loadingBudget,
      isLoadingBudgetDetail,
      isLoadingCalcs,
      hasNegotiations,
      hasClosings,
      projectId,
    ]
  );

  const servicesValue = useMemo<BudgetServicesSlice>(() => {
    const budgetServices = activeBudget?.budgetServices ?? [];
    // Gera UUID temporário estável se o orçamento não tiver _id (ex: duplicata antes do save).
    // Evita mistura de serviços entre orçamentos duplicados com mesmo fornecedor.
    if (activeBudget?._id) {
      tempBudgetIdRef.current = null;
    } else if (!tempBudgetIdRef.current) {
      tempBudgetIdRef.current = `temp-${uuidv4()}`;
    }
    const budgetId = activeBudget?._id ?? tempBudgetIdRef.current!;
    const servicesById: Record<string, ServiceData> = {};
    const servicesByGroupKey: Record<string, ServiceData[]> = {};

    budgetServices.forEach((service) => {
      if (service._id) {
        servicesById[service._id] = service;
      }

      // Usar Set para garantir versões únicas e evitar duplicações
      const uniqueVersions = Array.from(new Set(service.versions ?? []));

      uniqueVersions.forEach((version) => {
        const versionStatus =
          version === 0
            ? "OPEN"
            : activeBudget?.versionHistory?.find(
                (entry) => entry.version === version
              )?.status || "NEGOTIATE";
        const key = getBudgetServiceGroupKey(
          budgetId,
          service.supplierId,
          service.type,
          resolveServiceCalcOption(service, {
            version,
            status: versionStatus,
          }),
          version
        );
        if (!servicesByGroupKey[key]) {
          servicesByGroupKey[key] = [];
        }

        // Verificar se serviço já existe na chave antes de adicionar
        // Se o serviço tem _id, compara por _id; caso contrário, adiciona sempre
        const alreadyExists = service._id
          ? servicesByGroupKey[key].some((s) => s._id === service._id)
          : false;

        if (!alreadyExists) {
          servicesByGroupKey[key].push(service);
        }
      });
    });

    return { budgetServices, servicesById, servicesByGroupKey };
  }, [
    activeBudget?.budgetServices,
    activeBudget?._id,
    activeBudget?.versionHistory,
  ]);

  const suppliersValue = useMemo<BudgetSuppliersSlice>(
    () => ({
      supplierList: activeBudget?.supplierList ?? [],
      budgetGroups: activeBudget?.budgetGroups ?? [],
      addedItemTypeList: activeBudget?.addedItemTypeList ?? [],
      baseValues: activeBudget?.baseValues ?? [],
      mainAppliedTaxes: activeBudget?.mainAppliedTaxes ?? [],
      paymentSolicitation: activeBudget?.paymentSolicitation ?? [],
      files: activeBudget?.files ?? [],
      budgetWideAppliedTaxes: activeBudget?.budgetWideAppliedTaxes ?? [],
      itemTypeList:
        activeBudget?.itemTypeList ?? ({} as ItemTypeListPerCalcOption),
    }),
    [
      activeBudget?.supplierList,
      activeBudget?.budgetGroups,
      activeBudget?.addedItemTypeList,
      activeBudget?.baseValues,
      activeBudget?.mainAppliedTaxes,
      activeBudget?.paymentSolicitation,
      activeBudget?.files,
      activeBudget?.budgetWideAppliedTaxes,
      activeBudget?.itemTypeList,
    ]
  );

  const totalsValue = useMemo<BudgetTotalsSlice>(
    () => ({
      budgetValuesTotal:
        activeBudget?.budgetValuesTotal ?? ({} as VersionTotals),
      negociationValuesTotal: activeBudget?.negociationValuesTotal ?? [],
      closingValuesTotal:
        activeBudget?.closingValuesTotal ?? ({} as VersionTotals),
    }),
    [
      activeBudget?.budgetValuesTotal,
      activeBudget?.negociationValuesTotal,
      activeBudget?.closingValuesTotal,
    ]
  );

  const uiValue = useMemo<BudgetUISlice>(
    () => ({
      notification,
      budgetAutosaveStatus,
      hasPendingBudgetChanges,
      hasBudgetAutosaveFailure,
      budgetAutosaveSuccessCount,
      isBudgetDataModalOpen,
      categoriesSnapshot,
      budgetList,
      temp,
    }),
    [
      notification,
      budgetAutosaveStatus,
      hasPendingBudgetChanges,
      hasBudgetAutosaveFailure,
      budgetAutosaveSuccessCount,
      isBudgetDataModalOpen,
      categoriesSnapshot,
      budgetList,
    ]
  );

  // -----------------------------------------------------------------------
  // Stable actions slice. Several action handlers (createBudget, updateBudget,
  // loadBudgetList, etc.) are declared as plain `async function` inside the
  // component body, so their identity changes on every render. To prevent
  // every consumer of `useBudgetActions()` from re-rendering on each
  // keystroke, we keep a ref to the latest closures and expose stable
  // wrappers that read through it. The wrappers themselves are created once
  // (`useMemo([])`) and never change identity.
  // -----------------------------------------------------------------------

  const latestActionsRef = useRef({
    activeBudgetDispatch,
    setNegotiationVersion,
    setActiveCalcOption,
    setDetailedBudget,
    setVersionInfo,
    setLoadingBudget,
    setIsLoadingCalcs,
    setIsBudgetDataModalOpen,
    setCategoriesSnapshot,
    runBudgetAutosave,
    createBudget,
    updateBudget,
    loadBudgetList,
    hydrateActiveBudget,
    clearBudget,
    createEmptyBudget,
    closeNotification,
    showNotification,
    handleTempTaxData,
    cleanTempTaxData,
  });
  latestActionsRef.current = {
    activeBudgetDispatch,
    setNegotiationVersion,
    setActiveCalcOption,
    setDetailedBudget,
    setVersionInfo,
    setLoadingBudget,
    setIsLoadingCalcs,
    setIsBudgetDataModalOpen,
    setCategoriesSnapshot,
    runBudgetAutosave,
    createBudget,
    updateBudget,
    loadBudgetList,
    hydrateActiveBudget,
    clearBudget,
    createEmptyBudget,
    closeNotification,
    showNotification,
    handleTempTaxData,
    cleanTempTaxData,
  };

  const actionsValue = useMemo<BudgetActionsSlice>(
    () => ({
      activeBudgetDispatch: ((action) =>
        latestActionsRef.current.activeBudgetDispatch(
          action
        )) as Dispatch<ActiveBudgetAction>,
      setNegotiationVersion: (v) =>
        latestActionsRef.current.setNegotiationVersion(v),
      setActiveCalcOption: (o) =>
        latestActionsRef.current.setActiveCalcOption(o),
      setDetailedBudget: (id) => latestActionsRef.current.setDetailedBudget(id),
      setVersionInfo: (vi) => latestActionsRef.current.setVersionInfo(vi),
      setLoadingBudget: (v) => latestActionsRef.current.setLoadingBudget(v),
      setIsLoadingCalcs: (v) => latestActionsRef.current.setIsLoadingCalcs(v),
      setIsBudgetDataModalOpen: (open) =>
        latestActionsRef.current.setIsBudgetDataModalOpen(open),
      setCategoriesSnapshot: (snap) =>
        latestActionsRef.current.setCategoriesSnapshot(snap),
      runBudgetAutosave: (source, audit) =>
        latestActionsRef.current.runBudgetAutosave(source, audit),
      createBudget: (data) => latestActionsRef.current.createBudget(data),
      updateBudget: (id, data, action, version) =>
        latestActionsRef.current.updateBudget(id, data, action, version),
      loadBudgetList: (pid) => latestActionsRef.current.loadBudgetList(pid),
      hydrateActiveBudget: (item, stageVersion) =>
        latestActionsRef.current.hydrateActiveBudget(item, stageVersion),
      clearBudget: () => latestActionsRef.current.clearBudget(),
      createEmptyBudget: (type: BudgetType) =>
        latestActionsRef.current.createEmptyBudget(type),
      closeNotification: () => latestActionsRef.current.closeNotification(),
      showNotification: (message, severity) =>
        latestActionsRef.current.showNotification(message, severity),
      handleTempTaxData: (value, type) =>
        latestActionsRef.current.handleTempTaxData(value, type),
      cleanTempTaxData: () => latestActionsRef.current.cleanTempTaxData(),
    }),
    []
  );

  // -----------------------------------------------------------------------
  // Lifted supplier lookups. Previously every BudgetSupplier / ServiceSupplier
  // / SupplierAutoComplete row mounted its own `useQuery(["provider", id])`,
  // adding N hooks (and N reducer subscriptions) to the tree even though
  // react-query dedupes the network call. We now run a single `useQueries`
  // here keyed by the unique parsed supplier ids in the active budget and
  // expose the resulting map via `BudgetProvidersContext`. Consumers read
  // their entry with `useBudgetProvider(id)` — one lookup, no extra hooks.
  // -----------------------------------------------------------------------

  const uniqueSupplierIds = useMemo<string[]>(() => {
    const list = activeBudget?.supplierList;
    if (!Array.isArray(list) || list.length === 0) return [];
    const seen = new Set<string>();
    for (const entry of list) {
      const parsed = parseSupplierIdForProviderLookup(entry?.supplierId);
      // Skip empty ids and locally-generated temporary ids (which contain a
      // dash). The original per-row queries also bailed via
      // `SupplierService.getProviders` returning early for empty `id`.
      if (!parsed) continue;
      if (parsed.includes("-")) continue;
      seen.add(parsed);
    }
    // Sorting keeps the `useQueries` slot order stable across renders so
    // react's hook ordering invariant is respected even when the user
    // adds/removes suppliers in arbitrary positions.
    return Array.from(seen).sort();
  }, [activeBudget?.supplierList]);

  const providerQueries = useQueries(
    uniqueSupplierIds.map((id) => ({
      queryKey: ["provider", id],
      queryFn: () => SupplierService.getProviders({ id }),
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }))
  );

  const providersValue = useMemo<BudgetProvidersSlice>(() => {
    const map: Record<string, any> = {};
    let isLoadingProviders = false;
    uniqueSupplierIds.forEach((id, index) => {
      const result = providerQueries[index];
      if (!result) return;
      if (result.isLoading) isLoadingProviders = true;
      const data = result.data as any[] | undefined;
      if (Array.isArray(data) && data.length > 0) {
        map[id] = data[0];
      }
    });
    return { providersById: map, isLoadingProviders };
  }, [
    uniqueSupplierIds,
    // Fixed-size deps: react-query recreates result objects each render, but
    // spreading `.map()` into this array changes its length when supplier ids
    // are added/removed — which violates the rules of hooks.
    providerQueries
      .map((q, index) => {
        const data = q?.data as any[] | undefined;
        const firstId =
          Array.isArray(data) && data.length > 0 ? data[0]?._id : null;
        return `${uniqueSupplierIds[index]}:${q?.isLoading}:${q?.isFetching}:${
          q?.dataUpdatedAt ?? 0
        }:${firstId ?? ""}`;
      })
      .join("\0"),
  ]);

  return (
    <BudgetContext.Provider
      value={{
        isLoadingCalcs,
        notification,
        loadingBudget,
        isLoadingBudgetDetail,
        projectId,
        budgetList,
        activeBudget,
        negotiationVersion,
        activeCalcOption,
        activeBudgetIndex,
        hasClosings,
        hasNegotiations,
        detailedBudget,
        versionInfo,
        temp: temp,
        isBudgetDisabled,
        categoriesSnapshot,
        budgetAutosaveStatus,
        hasPendingBudgetChanges,
        hasBudgetAutosaveFailure,
        budgetAutosaveSuccessCount,
        isBudgetDataModalOpen,
        setIsBudgetDataModalOpen,
        runBudgetAutosave,
        setDetailedBudget,
        setActiveCalcOption,
        setNegotiationVersion,
        createBudget,
        updateBudget,
        loadBudgetList,
        hydrateActiveBudget,
        closeNotification,
        showNotification,
        clearBudget,
        createEmptyBudget,
        setIsLoadingCalcs,
        activeBudgetDispatch,
        setLoadingBudget,
        setVersionInfo,
        handleTempTaxData,
        cleanTempTaxData,
        setCategoriesSnapshot,
        closingControl: resolvedClosingControl,
        isLoadingClosingControl,
        isFetchingClosingControl,
        isClosingControlError,
        refetchClosingControl,
        applyClosingControlState,
        resolveProjectClosingStatus,
      }}
    >
      <BudgetMetaContext.Provider value={metaValue}>
        <BudgetServicesContext.Provider value={servicesValue}>
          <BudgetSuppliersContext.Provider value={suppliersValue}>
            <BudgetTotalsContext.Provider value={totalsValue}>
              <BudgetActionsContext.Provider value={actionsValue}>
                <BudgetUIContext.Provider value={uiValue}>
                  <BudgetProvidersContext.Provider value={providersValue}>
                    {children}
                  </BudgetProvidersContext.Provider>
                </BudgetUIContext.Provider>
              </BudgetActionsContext.Provider>
            </BudgetTotalsContext.Provider>
          </BudgetSuppliersContext.Provider>
        </BudgetServicesContext.Provider>
      </BudgetMetaContext.Provider>
    </BudgetContext.Provider>
  );
}

const useBudget = () => useContext(BudgetContext);

// -------------------------------------------------------------------------
// Slice hooks.
//
// Prefer these over `useBudget()` in new code (and especially in hot-path
// components that re-render on every keystroke). Each one subscribes to a
// single `BudgetXContext` whose value reference only changes when the
// underlying slice does, so a typing edit that only touches `budgetServices`
// no longer wakes up consumers of the meta / suppliers / totals / actions
// slices.
// -------------------------------------------------------------------------

const useBudgetMeta = () => useContext(BudgetMetaContext);
const useBudgetServicesState = () => useContext(BudgetServicesContext);
const useBudgetSuppliersState = () => useContext(BudgetSuppliersContext);
const useBudgetTotals = () => useContext(BudgetTotalsContext);
const useBudgetActions = () => useContext(BudgetActionsContext);
const useBudgetUI = () => useContext(BudgetUIContext);
const useBudgetProviders = () => useContext(BudgetProvidersContext);

/**
 * Returns the cached supplier metadata for a given raw supplier id, applying
 * the same parsing rules the per-row `useQuery(["provider", id])` callers
 * used (split on `|` then on `-`). Components should pass `supplierId` as
 * stored in `supplierList`; the hook handles the rest.
 */
const useBudgetProvider = (rawSupplierId: string | undefined): any => {
  const { providersById } = useContext(BudgetProvidersContext);
  if (!rawSupplierId) return undefined;
  const key = parseSupplierIdForProviderLookup(rawSupplierId);
  return providersById[key];
};

export {
  BudgetProvider,
  getBudgetServiceGroupKey,
  useBudget,
  useBudgetMeta,
  useBudgetServicesState,
  useBudgetSuppliersState,
  useBudgetTotals,
  useBudgetActions,
  useBudgetUI,
  useBudgetProviders,
  useBudgetProvider,
};

function useClosingControl() {
  const {
    closingControl,
    isLoadingClosingControl,
    isFetchingClosingControl,
    isClosingControlError,
    refetchClosingControl,
    applyClosingControlState,
  } = useBudget();

  return {
    closingControl,
    isLoadingClosingControl,
    isFetchingClosingControl,
    isClosingControlError,
    isClosingTabActive: true,
    refetchClosingControl,
    applyClosingControlState,
  };
}

export { useClosingControl };
